import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  AppNode,
  AppLink,
  FunnelParameters,
  FunnelStage,
  PresetFlow,
  UserProfile,
  UserRole,
  AuthFlowState,
  Project,
  TeamMember,
  CrmOpportunity,
  Sponsor,
  StageActivation,
  ContentItem,
  DigitalAiAsset,
  LibraryItem,
  TestExecutionResult,
  EventOperationsState,
  EventPortfolioState,
  SponsorOperationsState,
  UserPermissions,
  OnboardingAnswers,
  ToolTemplate
} from './types';
import {
  PRESET_FLOWS,
  DEFAULT_PARAMETERS,
  AVAILABLE_APP_TEMPLATES
} from './presets';
import { normalizeEventOperationsState } from './lib/eventOperations';
import { createBlankEventOperationsState, hasLegacyEventData, mapEventPlanToStudio } from './lib/eventExperience';
import {
  addEventToPortfolio,
  createEventPortfolioState,
  getActiveEvent,
  normalizeEventPortfolioState,
  removeEventFromPortfolio,
  replaceActiveEvent,
  selectEventInPortfolio,
} from './lib/eventPortfolio';
import { normalizeSponsorOperationsState } from './lib/sponsorOperations';
import { buildPublicOfferDocument, getPublicOfferUrl } from './lib/publicOffers';
import { AppIcon } from './components/AppIcon';
import { GrosymLogo } from './components/GrosymLogo';
import { ModuleExperienceHome, ModuleOperationsFrame } from './components/ModuleExperienceHome';
import { sanitizeDigitalAiAssets } from './lib/digitalAi';
import { clampYToStage, getStageAtY, moveNodeToStage } from './lib/funnelPlacement';
import type { GeneratedResultItem, AutomationType } from './components/FunnelAutomationModal';
import {
  loadProjectWorkspace,
  saveProjectWorkspace,
  loadProjectOpportunities,
  saveProjectOpportunities,
  loadProjectStudio,
  saveProjectStudio,
  loadProjectSponsors,
  saveProjectSponsors,
  loadProjectSponsorOperations,
  saveProjectSponsorOperations,
  loadProjectActivations,
  saveProjectActivations,
  loadProjectAiApps,
  saveProjectAiApps,
  loadProjectAiExecutionHistory,
  saveProjectAiExecutionHistory,
  loadProjectLibrary,
  saveProjectLibrary,
  loadProjectEventOperations,
  saveProjectEventOperations,
  loadProjectEventPortfolio,
  saveProjectEventPortfolio,
  publishPublicOffer,
  unpublishPublicOffer,
  checkLegacyLocalStorage,
  executeLegacyMigration,
  validateUserAccessAndMembership,
  fetchUserPermissionsFromDb,
  requestAppActivation,
  saveUserOnboardingData,
  loadUserCustomFlow
} from './services/firestoreSync';
import { subscribeToAuth, logOutFromFirebase } from './services/auth';
import {
  Plus,
  Trash2,
  X,
  Sliders,
  Eye,
  EyeOff,
  User,
  Users,
  Sparkles,
  Mic,
  Loader2,
  Check,
  ChevronRight,
  TrendingUp,
  Award,
  Layers,
  Zap,
  Menu,
  ChevronDown,
  Info,
  PlusCircle,
  ChevronLeft,
  HelpCircle,
  Link as LinkIcon,
  CheckCircle2,
  BrainCircuit,
  FileSpreadsheet,
  MoveHorizontal,
  MoveVertical,
  AlignJustify,
  UserCheck,
  GraduationCap,
  PackageCheck,
  Megaphone,
  UserPlus,
  MessageSquareHeart,
  Video,
  Rocket,
  Search,
  CheckSquare,
  Undo2,
  Redo2,
  Copy,
  ClipboardPaste,
  GripHorizontal,
  Lock,
  Unlock,
  Database,
  ShieldCheck,
  Pencil,
  DollarSign,
  Briefcase,
  Kanban,
  Clapperboard,
  Cpu,
  Flame,
  BarChart3,
  ZoomIn,
  ZoomOut,
  Minimize2,
  Maximize2,
  MoreVertical,
  Settings,
  Library,
  Paperclip,
  CalendarDays
} from 'lucide-react';

const CrmSection = React.lazy(() => import('./components/CrmSection').then(module => ({ default: module.CrmSection })));
const StudioSection = React.lazy(() => import('./components/StudioSection').then(module => ({ default: module.StudioSection })));
const DigitalAiAppsSection = React.lazy(() => import('./components/DigitalAiAppsSection').then(module => ({ default: module.DigitalAiAppsSection })));
const SponsorsSection = React.lazy(() => import('./components/SponsorsSection').then(module => ({ default: module.SponsorsSection })));
const MetricsSection = React.lazy(() => import('./components/MetricsSection').then(module => ({ default: module.MetricsSection })));
const EventExperience = React.lazy(() => import('./components/EventExperience').then(module => ({ default: module.EventExperience })));
const SimplePlanSection = React.lazy(() => import('./components/SimplePlanSection').then(module => ({ default: module.SimplePlanSection })));
const SidebarControls = React.lazy(() => import('./components/SidebarControls').then(module => ({ default: module.SidebarControls })));
const GoogleSheetsModal = React.lazy(() => import('./components/GoogleSheetsModal').then(module => ({ default: module.GoogleSheetsModal })));
const FirebaseSyncModal = React.lazy(() => import('./components/FirebaseSyncModal').then(module => ({ default: module.FirebaseSyncModal })));
const LoginModal = React.lazy(() => import('./components/LoginModal').then(module => ({ default: module.LoginModal })));
const UserProfileModal = React.lazy(() => import('./components/UserProfileModal').then(module => ({ default: module.UserProfileModal })));
const TeamMembersModal = React.lazy(() => import('./components/TeamMembersModal').then(module => ({ default: module.TeamMembersModal })));
const ToolModuleModal = React.lazy(() => import('./components/ToolModuleModal').then(module => ({ default: module.ToolModuleModal })));
const ToolCatalogModal = React.lazy(() => import('./components/ToolCatalogModal').then(module => ({ default: module.ToolCatalogModal })));
const OnboardingWizard = React.lazy(() => import('./components/OnboardingWizard').then(module => ({ default: module.OnboardingWizard })));
const FunnelAutomationModal = React.lazy(() => import('./components/FunnelAutomationModal').then(module => ({ default: module.FunnelAutomationModal })));
const StageActivationModal = React.lazy(() => import('./components/StageActivationModal').then(module => ({ default: module.StageActivationModal })));
const ProductTourTooltips = React.lazy(() => import('./components/ProductTourTooltips').then(module => ({ default: module.ProductTourTooltips })));

const STAGE_ICONS: Record<FunnelStage, React.ReactNode> = {
  personal: <UserCheck size={13} />,
  education: <GraduationCap size={13} />,
  offer: <PackageCheck size={13} />,
  attraction: <Megaphone size={13} />,
  registration: <UserPlus size={13} />,
  nurturing: <MessageSquareHeart size={13} />,
  attendance: <Video size={13} />,
  retention: <Award size={13} />,
  delivery: <Rocket size={13} />,
};

const ROMAN_NUMERALS = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX'];

const STAGES_ORDER: { stage: FunnelStage; label: string; bg: string; border: string; text: string; color: string; desc: string }[] = [
  { stage: 'personal', label: 'I. Dirección del Proyecto', bg: 'bg-rose-50/25', border: 'border-rose-100', text: 'text-rose-900', color: '#f43f5e', desc: 'Dirección estratégica, visión, liderazgo y enfoque ejecutivo.' },
  { stage: 'education', label: 'II. Educación y Aprendizaje', bg: 'bg-blue-50/20', border: 'border-blue-100', text: 'text-blue-900', color: '#3b82f6', desc: 'Maestría técnica, destrezas teóricas y certificaciones adquiridas.' },
  { stage: 'offer', label: 'III. Armado de la Oferta', bg: 'bg-violet-50/20', border: 'border-violet-100', text: 'text-violet-900', color: '#8b5cf6', desc: 'Diseño de valor, estructuración de ofertas irresistibles y ganchos.' },
  { stage: 'attraction', label: 'IV. Captación de Tráfico', bg: 'bg-emerald-50/25', border: 'border-emerald-100', text: 'text-emerald-900', color: '#00C57C', desc: 'Anuncios pagos en Meta/LinkedIn Ads, SEO y tráfico orgánico.' },
  { stage: 'nurturing', label: 'V. Nutrición & Relación', bg: 'bg-orange-50/20', border: 'border-orange-100', text: 'text-orange-900', color: '#ea580c', desc: 'WhatsApp Bots, automatización de correos y comunidades de valor.' },
  { stage: 'registration', label: 'VI. Proceso de Registro', bg: 'bg-indigo-50/20', border: 'border-indigo-100', text: 'text-indigo-900', color: '#6366f1', desc: 'Landing Pages con formularios de alta conversión y reservas.' },
  { stage: 'delivery', label: 'VII. Entrega del Servicio', bg: 'bg-teal-50/25', border: 'border-teal-100', text: 'text-teal-900', color: '#0d9488', desc: 'Portal LMS de alumnos, mentoría presencial, soporte técnico y entrega post-venta.' },
  { stage: 'attendance', label: 'VIII. Asistencia en Vivo', bg: 'bg-fuchsia-50/20', border: 'border-fuchsia-100', text: 'text-fuchsia-900', color: '#d946ef', desc: 'Webinars grupales, Masterclass en vivo o consultas privadas.' },
  { stage: 'retention', label: 'IX. Retención VIP', bg: 'bg-amber-50/25', border: 'border-amber-100', text: 'text-amber-900', color: '#eab308', desc: 'Membresías recurrentes, boletines mensuales y upsells anuales.' }
];

const FUNNEL_STAGE_ORDER = STAGES_ORDER.map((item) => item.stage);
const LEGACY_CRM_SAMPLE_IDS = new Set(['opp-1', 'opp-2', 'opp-3', 'opp-4', 'opp-5', 'opp-6', 'opp-7', 'opp-8', 'opp-9']);
const LEGACY_STUDIO_SAMPLE_IDS = new Set(['cnt-1', 'cnt-2', 'cnt-3', 'cnt-4', 'cnt-5', 'cnt-6']);
const LEGACY_SPONSOR_SAMPLE_IDS = new Set(['spon-1', 'spon-2', 'spon-3', 'spon-4', 'spon-5']);

const DEFAULT_TEAM_MEMBERS: TeamMember[] = [];

const EMPTY_SPONSOR_OPERATIONS: SponsorOperationsState = {
  version: 1,
  dataSource: 'provider',
  brands: [],
  contacts: [],
  opportunities: [],
  inventory: [],
  activations: [],
  activities: [],
  reports: [],
  renewals: [],
  objections: [],
  updatedAt: new Date().toISOString(),
};

const isLegacyGeneratedCanvasItem = (id: unknown) => {
  const value = String(id || '');
  return value.startsWith('demo-') || value.startsWith('node-p-') || value.startsWith('link-p-');
};

const cleanSponsorDemoRecords = (state: SponsorOperationsState): SponsorOperationsState => {
  const demoBrandIds = new Set([...LEGACY_SPONSOR_SAMPLE_IDS, 'brand-farmacity']);
  const demoInventoryIds = new Set(['inventory-line-d', 'inventory-top-stations', 'inventory-network']);
  const demoOpportunityIds = new Set(state.opportunities.filter((item) => demoBrandIds.has(item.brandId)).map((item) => item.id));
  const demoActivationIds = new Set(state.activations.filter((item) => demoBrandIds.has(item.brandId) || demoOpportunityIds.has(item.opportunityId)).map((item) => item.id));
  const opportunities = state.opportunities.filter((item) => !demoBrandIds.has(item.brandId));
  return {
    ...state,
    dataSource: 'provider',
    brands: state.brands.filter((item) => !demoBrandIds.has(item.id)),
    contacts: state.contacts.filter((item) => !demoBrandIds.has(item.brandId)),
    opportunities,
    inventory: state.inventory.filter((item) => !demoInventoryIds.has(item.id)),
    activations: state.activations.filter((item) => !demoActivationIds.has(item.id)),
    activities: state.activities.filter((item) => !demoOpportunityIds.has(item.opportunityId)),
    reports: state.reports.filter((item) => !demoBrandIds.has(item.brandId) && !demoOpportunityIds.has(item.opportunityId)),
    renewals: state.renewals.filter((item) => !demoOpportunityIds.has(item.sourceOpportunityId) && !demoOpportunityIds.has(item.renewalOpportunityId || '')),
    objections: opportunities.length ? state.objections : [],
  };
};

const cleanEmptyEventConfiguration = (candidate: EventOperationsState): EventOperationsState => {
  const blank = createBlankEventOperationsState(candidate.updatedAt, candidate.event.id);
  const generatedContentIds = new Set([
    'content-authority', 'content-invitation', 'content-benefits',
    'content-reminder', 'content-coverage', 'content-follow-up',
  ]);
  const hasOnlyGeneratedContent = Boolean(candidate.contentPlan?.length)
    && candidate.contentPlan!.every((item) => generatedContentIds.has(item.id));
  // A completed content plan cannot legitimately exist before the required
  // offer name. This combination came from the former demo bootstrap.
  if (!candidate.event.name.trim() && hasOnlyGeneratedContent) return blank;
  const hasGuidedAnswers = Object.values(candidate.guidedSetup?.answers || {}).some((value) => (
    Array.isArray(value) ? value.length > 0 : value !== null && value !== undefined && String(value).trim() !== ''
  ));
  const hasConnectedData = hasLegacyEventData(candidate)
    || candidate.orders.length > 0
    || candidate.payments.length > 0
    || candidate.products?.length > 0
    || candidate.partners?.length > 0
    || candidate.eventSponsorIds?.length > 0;
  if (hasGuidedAnswers || hasConnectedData) return candidate;
  return blank;
};


// --- RESTRICTED APPLICATION GATING VIEW ---
interface RestrictedAppViewProps {
  appKey: string;
  appName: string;
  categoryName: string;
  icon: React.ReactNode;
  userProfile: UserProfile;
  features: string[];
}

function RestrictedAppView({ appKey, appName, categoryName, icon, userProfile, features }: RestrictedAppViewProps) {
  const [requested, setRequested] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleRequestActivation = async () => {
    setIsSubmitting(true);
    try {
      await requestAppActivation(userProfile, appKey, appName);
      setRequested(true);
    } catch (_) {}
    setIsSubmitting(false);
  };

  return (
    <div className="flex-1 flex flex-col items-center justify-center p-6 bg-gradient-to-b from-slate-50 to-slate-100/60 overflow-y-auto text-slate-800">
      <div className="max-w-md w-full bg-white border border-slate-200/80 rounded-3xl p-8 shadow-xl text-center flex flex-col items-center gap-5 animate-scaleUp">
        <div className="w-16 h-16 rounded-2xl bg-amber-50 border border-amber-200 text-amber-600 flex items-center justify-center shadow-inner">
          {icon}
        </div>

        <div className="space-y-1.5">
          <span className="text-[11px] font-black uppercase tracking-widest text-amber-600 bg-amber-50 px-2.5 py-1 rounded-full border border-amber-200/60">
            {categoryName}
          </span>
          <h2 className="text-xl font-black text-slate-900 mt-2">{appName}</h2>
          <p className="text-xs text-slate-500 font-medium">
            Esta aplicación no está incluida en tu plan actual o requiere activación por parte del administrador.
          </p>
        </div>

        <div className="w-full bg-slate-50 rounded-2xl p-4 border border-slate-100 text-left space-y-2">
          <h4 className="text-[11px] font-bold text-slate-700 uppercase tracking-wider">Capacidades desbloqueables:</h4>
          <ul className="text-xs text-slate-600 space-y-1.5 font-medium">
            {features.map((f, i) => (
              <li key={i} className="flex items-center gap-2">
                <span className="text-emerald-500 font-bold">✓</span> {f}
              </li>
            ))}
          </ul>
        </div>

        <div className="w-full pt-2 flex flex-col gap-2.5">
          {requested ? (
            <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-bold py-3 px-4 rounded-xl flex items-center justify-center gap-2">
              <span>✓ Solicitud enviada. Un administrador activará tu acceso pronto.</span>
            </div>
          ) : (
            <button
              onClick={handleRequestActivation}
              disabled={isSubmitting}
              className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black py-3 px-5 rounded-xl text-xs transition-all shadow-md hover:shadow-lg hover:scale-102 flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
            >
              {isSubmitting ? 'Enviando solicitud...' : 'Solicitar Activación de Módulo'}
            </button>
          )}

          <a
            href={`https://wa.me/5491123456789?text=Hola,%20quisiera%20activar%20el%20m%C3%B3dulo%20de%20${encodeURIComponent(appName)}%20en%20Grosym%20Flow`}
            target="_blank"
            rel="noreferrer"
            className="text-[11px] text-slate-500 hover:text-emerald-600 font-bold transition-colors"
          >
            ¿Necesitas ayuda inmediata? Contactar soporte vía WhatsApp &rarr;
          </a>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  // --- STATE ---
  const [nodes, setNodes] = useState<AppNode[]>(() => {
    const saved = localStorage.getItem('grosym_nodes_cascade');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) return parsed.filter((node) => !isLegacyGeneratedCanvasItem(node?.id));
      } catch (e) {}
    }
    return PRESET_FLOWS[0]?.nodes || [];
  });

  const [links, setLinks] = useState<AppLink[]>(() => {
    const saved = localStorage.getItem('grosym_links_cascade');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) return parsed.filter((link) => !isLegacyGeneratedCanvasItem(link?.id));
      } catch (e) {}
    }
    return PRESET_FLOWS[0]?.links || [];
  });

  const [parameters, setParameters] = useState<FunnelParameters>(() => {
    const saved = localStorage.getItem('grosym_params_cascade');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        const savedNodes = JSON.parse(localStorage.getItem('grosym_nodes_cascade') || '[]');
        if (Array.isArray(savedNodes) && savedNodes.some((node) => isLegacyGeneratedCanvasItem(node?.id))) {
          return DEFAULT_PARAMETERS;
        }
        if (parsed && typeof parsed.traffic === 'number') return parsed;
      } catch (e) {}
    }
    return DEFAULT_PARAMETERS;
  });

  const [customToolTemplates, setCustomToolTemplates] = useState<ToolTemplate[]>([]);

  const [activeClientIndex, setActiveClientIndex] = useState<number>(0);

  // --- 2-CANVAS ARCHITECTURE (LIENZO 1: ENTRENAMIENTO / DEMO | LIENZO 2: NEGOCIO PERSONALIZADO) ---
  const [userCustomFlow, setUserCustomFlow] = useState<PresetFlow | null>(() => {
    try {
      const savedAuth = localStorage.getItem('grosym_user_auth');
      if (savedAuth) {
        const parsed = JSON.parse(savedAuth);
        const uid = parsed.uid || 'guest_user';
        const savedFlow = localStorage.getItem(`grosym_custom_flow_${uid}`) || localStorage.getItem('grosym_custom_flow');
        if (savedFlow) {
          const parsedFlow = JSON.parse(savedFlow);
          if (parsedFlow && parsedFlow.name && Array.isArray(parsedFlow.nodes)) return parsedFlow;
        }
      }
    } catch (_) {}
    return null;
  });


  const allAvailableFlows = useMemo<PresetFlow[]>(() => {
    const flows: PresetFlow[] = [PRESET_FLOWS[0]];
    if (userCustomFlow) {
      flows.push(userCustomFlow);
    }
    return flows;
  }, [userCustomFlow]);

  const clientNames = useMemo<string[]>(() => (
    userCustomFlow ? [PRESET_FLOWS[0].name, userCustomFlow.name] : [PRESET_FLOWS[0].name]
  ), [userCustomFlow]);

  const [editingClientIndex, setEditingClientIndex] = useState<number | null>(null);
  const [tempClientName, setTempClientName] = useState<string>('');

  const handleSaveClientName = () => {
    if (editingClientIndex === null || !tempClientName.trim()) return;

    if (editingClientIndex === 1 && userCustomFlow) {
      const updatedFlow = {
        ...userCustomFlow,
        name: tempClientName.trim()
      };
      setUserCustomFlow(updatedFlow);
      const uid = userProfile?.uid || 'guest_user';
      try {
        localStorage.setItem(`grosym_custom_flow_${uid}`, JSON.stringify(updatedFlow));
      } catch (_) {}
    }

    setEditingClientIndex(null);
  };

  // Delete project confirmation state
  const [projectToDeleteIndex, setProjectToDeleteIndex] = useState<number | null>(null);

  const handleDeleteProject = (indexToDelete: number) => {
    if (indexToDelete === 1) {
      setUserCustomFlow(null);
      const uid = userProfile?.uid || 'guest_user';
      try {
        localStorage.removeItem(`grosym_custom_flow_${uid}`);
      } catch (_) {}
      
      setActiveClientIndex(0);
      const defaultFlow = PRESET_FLOWS[0];
      setNodes(defaultFlow.nodes);
      setLinks(defaultFlow.links);
      setParameters(defaultFlow.parameters);
      setSelectedNodeIds([]);
      setActiveLayer('global');
      saveClientState(defaultFlow.nodes, defaultFlow.links, defaultFlow.parameters, 0, 'global');
    }
    setProjectToDeleteIndex(null);
  };

  const [teamMembers, setTeamMembers] = useState<TeamMember[]>(() => {
    const saved = localStorage.getItem('grosym_team_members');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          const sampleIds = new Set(['team-1', 'team-2', 'team-3', 'team-4', 'team-5']);
          return parsed.filter((member) => !sampleIds.has(String(member?.id || '')));
        }
      } catch (e) {}
    }
    return DEFAULT_TEAM_MEMBERS;
  });

  const [showTeamModal, setShowTeamModal] = useState<boolean>(false);
  const [configureNodeModal, setConfigureNodeModal] = useState<AppNode | null>(null);

  useEffect(() => {
    localStorage.setItem('grosym_team_members', JSON.stringify(teamMembers));
  }, [teamMembers]);

  // Auth & Multi-tenant State Machine
  const [authFlowState, setAuthFlowState] = useState<AuthFlowState>('checking_auth');
  const [accessDeniedReason, setAccessDeniedReason] = useState<string>('');
  const [isHydrated, setIsHydrated] = useState<boolean>(false);
  const [cloudHydrationHealthy, setCloudHydrationHealthy] = useState<boolean>(false);
  const [showLoginModal, setShowLoginModal] = useState<boolean>(false);
  const [userProfile, setUserProfile] = useState<UserProfile>(() => {
    try {
      const saved = localStorage.getItem('grosym_user_auth');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && parsed.email) {
          const email = (parsed.email || '').toLowerCase().trim();
          const isSys = email === 'wilmer@grosym.site' || email.includes('admin') || email.includes('grosym.site') || email === 'regaliciaa@gmail.com' || email === 'wilmer.molina@tcs.com';
          return {
            ...parsed,
            role: isSys ? 'owner' : (parsed.role || 'viewer'),
            permissions: isSys ? { flow: true, studio: true, aiapps: true, sponsors: true, events: true } : parsed.permissions,
          };
        }
      }
    } catch (_) {}
    return {
      uid: 'guest_user',
      email: 'invitado@grosym.com',
      name: 'Usuario Invitado',
      role: 'viewer',
      organizationId: 'org_demo',
      organizationName: 'Espacio Demo',
      activeProjectId: 'proj_demo',
      isLoggedIn: false,
    };
  });
  const [currentProjects, setCurrentProjects] = useState<Project[]>([]);
  const [activeProjectId, setActiveProjectId] = useState<string>('');

  // --- GRANULAR APP ACCESS PERMISSIONS RESOLUTION ---
  const isSystemAdmin = useMemo(() => {
    const email = (userProfile?.email || '').toLowerCase().trim();
    return email === 'wilmer@grosym.site' ||
      email === 'regaliciaa@gmail.com' ||
      email === 'wilmer.molina@tcs.com' ||
      email.endsWith('@grosym.site') ||
      email.endsWith('@grosym.com') ||
      email.includes('admin');
  }, [userProfile?.email]);

  const isMaster = isSystemAdmin || userProfile?.role === 'owner';
  const canAccessFlow = isMaster || userProfile?.permissions?.flow !== false;
  const canAccessStudio = isMaster || userProfile?.permissions?.studio !== false;
  const canAccessDigital = isMaster || (userProfile?.permissions?.aiapps !== false && (userProfile?.permissions as any)?.ai_apps !== false);
  const canAccessSponsors = isMaster || userProfile?.permissions?.sponsors !== false;
  const canAccessEvents = isMaster || userProfile?.permissions?.events !== false;

  // Synchronize fresh permissions and user custom flow (Lienzo 2) from Firestore
  useEffect(() => {
    if (!userProfile?.email || userProfile.email === 'invitado@grosym.com') return;
    
    // 1. Sync permissions
    fetchUserPermissionsFromDb(userProfile.email, userProfile.uid).then((freshPerms) => {
      setUserProfile((prev) => {
        if (!prev) return prev;
        if (
          prev.permissions &&
          prev.permissions.flow === freshPerms.flow &&
          prev.permissions.studio === freshPerms.studio &&
          prev.permissions.aiapps === freshPerms.aiapps &&
          prev.permissions.sponsors === freshPerms.sponsors &&
          prev.permissions.events === freshPerms.events
        ) {
          return prev;
        }
        const updated = { ...prev, permissions: freshPerms };
        try {
          localStorage.setItem('grosym_user_auth', JSON.stringify(updated));
        } catch (_) {}
        return updated;
      });
    });

    // 2. Load custom flow (Lienzo 2)
    loadUserCustomFlow(userProfile.uid, userProfile.organizationId).then((loadedCustomFlow) => {
      if (loadedCustomFlow) {
        setUserCustomFlow(loadedCustomFlow);
      }
    });

    // 3. ONBOARDING 1: Bienvenida e Introducción a Grosym Flow (Ni bien abre Flow por primera vez)
    const welcomeKey = 'grosym_flow_welcome_seen_' + userProfile.uid;
    const hasSeenWelcome = localStorage.getItem(welcomeKey);
    if (!hasSeenWelcome && userProfile.isLoggedIn) {
      localStorage.setItem(welcomeKey, 'true');
      setShowProductTour(true);
    }
  }, [userProfile?.email, userProfile?.uid, userProfile?.organizationId]);
  const [legacyMigrationData, setLegacyMigrationData] = useState<{
    hasLegacyData: boolean;
    detectedItems: {
      customClientsCount: number;
      opportunitiesCount: number;
      studioCount: number;
      digitalAiCount: number;
    };
  }>({
    hasLegacyData: false,
    detectedItems: { customClientsCount: 0, opportunitiesCount: 0, studioCount: 0, digitalAiCount: 0 }
  });
  const [migrationRunning, setMigrationRunning] = useState<boolean>(false);
  const [migrationFeedback, setMigrationFeedback] = useState<{
    countImported: number;
    countFailed: number;
    backupKey: string;
  } | null>(null);
  const [showLegacyMigrationModal, setShowLegacyMigrationModal] = useState<boolean>(false);

  const [showGoogleSheetsModal, setShowGoogleSheetsModal] = useState<boolean>(false);
  const [showFirebaseModal, setShowFirebaseModal] = useState<boolean>(false);
  const [showUserProfileModal, setShowUserProfileModal] = useState<boolean>(false);
  const [showOnboardingWizard, setShowOnboardingWizard] = useState<boolean>(false);
  const [showProductTour, setShowProductTour] = useState<boolean>(false);
  const [isHeaderMenuOpen, setIsHeaderMenuOpen] = useState<boolean>(false);
  const headerMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (headerMenuRef.current && !headerMenuRef.current.contains(event.target as Node)) {
        setIsHeaderMenuOpen(false);
      }
    };
    if (isHeaderMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isHeaderMenuOpen]);

  // Zoom & Canvas scale state (with auto-canvas expansion on zoom out)
  const [canvasScale, setCanvasScale] = useState<number>(() => {
    const saved = localStorage.getItem('grosym_canvas_scale');
    if (saved) {
      const parsed = parseFloat(saved);
      if (!isNaN(parsed) && parsed >= 0.3 && parsed <= 1.6) return parsed;
    }
    return 1;
  });

  const handleToggleCanvasScale = () => {
    setCanvasScale(prev => {
      const next = prev <= 0.7 ? 1 : 0.65;
      localStorage.setItem('grosym_canvas_scale', next.toString());
      return next;
    });
  };

  const handleStepCanvasScale = (delta: number) => {
    setCanvasScale(prev => {
      const next = Math.max(0.35, Math.min(1.5, +(prev + delta).toFixed(2)));
      localStorage.setItem('grosym_canvas_scale', next.toString());
      return next;
    });
  };

  // Automated Action Buttons & Results Inbox State
  const [automationModalType, setAutomationModalType] = useState<AutomationType | null>(null);
  const [automationModalStage, setAutomationModalStage] = useState<FunnelStage | 'global'>('global');
  const [generatedResults, setGeneratedResults] = useState<GeneratedResultItem[]>(() => {
    const saved = localStorage.getItem('grosym_generated_results');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) return parsed.filter((item) => !String(item?.id || '').startsWith('res-init-'));
      } catch (e) {}
    }
    return [];
  });

  // ==========================================
  // STAGE ACTIVATIONS STATE & HANDLERS (CUANDO X -> HACEMOS Y)
  // ==========================================
  const DEFAULT_STAGE_ACTIVATIONS: StageActivation[] = [];

  const [activations, setActivations] = useState<StageActivation[]>(() => {
    const saved = localStorage.getItem('grosym_stage_activations');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          const sampleIds = new Set(['act-registro-bienvenida', 'act-nutricion-recordatorio', 'act-asistencia-oferta']);
          return parsed.filter((item) => !sampleIds.has(String(item?.id || '')));
        }
      } catch (e) {}
    }
    return DEFAULT_STAGE_ACTIVATIONS;
  });

  const [activationModalOpen, setActivationModalOpen] = useState(false);
  const [activationModalStage, setActivationModalStage] = useState<FunnelStage>('registration');
  const [activationModalTargetStage, setActivationModalTargetStage] = useState<FunnelStage | undefined>(undefined);
  const [editingActivation, setEditingActivation] = useState<StageActivation | null>(null);
  const [activationPickerStage, setActivationPickerStage] = useState<FunnelStage | null>(null);

  const handleOpenAddActivation = (stage: FunnelStage, targetStage?: FunnelStage) => {
    setActivationModalStage(stage);
    setActivationModalTargetStage(targetStage);
    setEditingActivation(null);
    setActivationModalOpen(true);
  };

  const handleOpenEditActivation = (activation: StageActivation) => {
    setActivationModalStage(activation.stage);
    setActivationModalTargetStage(activation.targetStage);
    setEditingActivation(activation);
    setActivationModalOpen(true);
  };

  const handleSaveActivation = (savedAct: StageActivation) => {
    setActivations(prev => {
      const exists = prev.some(a => a.id === savedAct.id);
      const updated = exists ? prev.map(a => a.id === savedAct.id ? savedAct : a) : [...prev, savedAct];
      localStorage.setItem('grosym_stage_activations', JSON.stringify(updated));
      return updated;
    });
  };

  const handleDeleteActivation = (id: string) => {
    setActivations(prev => {
      const updated = prev.filter(a => a.id !== id);
      localStorage.setItem('grosym_stage_activations', JSON.stringify(updated));
      return updated;
    });
  };

  // ==========================================
  // STUDIO CONTENT ITEMS STATE & SYNC
  // ==========================================
  const [studioItems, setStudioItems] = useState<ContentItem[]>(() => {
    const saved = localStorage.getItem('grosym_studio_content');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          return parsed.filter((item) => !LEGACY_STUDIO_SAMPLE_IDS.has(item?.id));
        }
      } catch (e) {}
    }
    return [];
  });

  const handleUpdateStudioItems = (newItems: ContentItem[]) => {
    setStudioItems(newItems);
    localStorage.setItem('grosym_studio_content', JSON.stringify(newItems));
  };

  const [digitalAiAssets, setDigitalAiAssets] = useState<DigitalAiAsset[]>(() => {
    const saved = localStorage.getItem('grosym_digital_ai_assets');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        const sanitized = sanitizeDigitalAiAssets(parsed);
        if (sanitized) return sanitized.filter((item) => item.dataSource !== 'demo');
      } catch (_) {}
    }
    return [];
  });

  const handleUpdateDigitalAiAssets = (items: DigitalAiAsset[]) => {
    setDigitalAiAssets(items);
    localStorage.setItem('grosym_digital_ai_assets', JSON.stringify(items));
  };

  const [digitalAiExecutionHistory, setDigitalAiExecutionHistory] = useState<TestExecutionResult[]>(() => {
    try {
      const parsed = JSON.parse(localStorage.getItem('grosym_digital_ai_history') || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  });

  const handleUpdateDigitalAiHistory = (history: TestExecutionResult[]) => {
    const boundedHistory = history.slice(0, 100);
    setDigitalAiExecutionHistory(boundedHistory);
    localStorage.setItem('grosym_digital_ai_history', JSON.stringify(boundedHistory));
  };

  const [libraryItems, setLibraryItems] = useState<LibraryItem[]>(() => {
    const saved = localStorage.getItem('grosym_custom_library_items');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) return parsed;
      } catch (_) {}
    }
    return [];
  });

  const handleUpdateLibraryItems = (items: LibraryItem[]) => {
    setLibraryItems(items);
    localStorage.setItem('grosym_custom_library_items', JSON.stringify(items));
  };


  const handleOpenAutomation = (type: AutomationType, stage: FunnelStage | 'global' = 'global') => {
    setAutomationModalType(type);
    setAutomationModalStage(stage);
  };

  const handleAddGeneratedResult = (newItem: GeneratedResultItem) => {
    const updated = [newItem, ...generatedResults];
    setGeneratedResults(updated);
    localStorage.setItem('grosym_generated_results', JSON.stringify(updated));
  };

  const handleDeleteResult = (id: string) => {
    const updated = generatedResults.filter(r => r.id !== id);
    setGeneratedResults(updated);
    localStorage.setItem('grosym_generated_results', JSON.stringify(updated));
  };

  const handleClearResults = () => {
    setGeneratedResults([]);
    localStorage.removeItem('grosym_generated_results');
  };

  const [voiceInlineStatus, setVoiceInlineStatus] = useState<'idle' | 'listening' | 'loading' | 'completed'>('idle');
  const [activeVoiceStage, setActiveVoiceStage] = useState<FunnelStage | null>(null);

  const handleTriggerVoiceCommand = (targetStage?: FunnelStage) => {
    if (voiceInlineStatus !== 'idle') return;

    const stage = targetStage || (activeLayer !== 'global' ? activeLayer : 'offer');
    setActiveVoiceStage(stage);

    // Step 1: Listening state with dots
    setVoiceInlineStatus('listening');

    setTimeout(() => {
      // Step 2: Loading state with spinner
      setVoiceInlineStatus('loading');

      setTimeout(() => {
        // Step 3: Automatically generate node/result completed
        const toolNames: Record<FunnelStage, string> = {
          personal: 'Rutina Ejecutiva (+1% Enfoque)',
          education: 'Masterclass de Alto Valor',
          offer: 'Propuesta Irresistible Grand Slam',
          attraction: 'Campaña Tráfico Meta Ads',
          nurturing: 'Secuencia VSL Nutrición',
          registration: 'Landing Registro Alta Conversión',
          attendance: 'Recordatorio WhatsApp Evento',
          delivery: 'Onboarding Automatizado Cliente',
          retention: 'Programa Recompra LTV'
        };

        const newToolName = toolNames[stage] || 'Herramienta de Optimización (+1%)';
        handleAddNodeFromVoice(stage, newToolName, 'Sparkles');

        setVoiceInlineStatus('completed');

        setTimeout(() => {
          setVoiceInlineStatus('idle');
          setActiveVoiceStage(null);
        }, 2000);
      }, 1500);
    }, 2500);
  };
  const [isSyncing, setIsSyncing] = useState<boolean>(false);
  const [isMobileUiHidden, setIsMobileUiHidden] = useState<boolean>(false);
  const [isHeaderVisible, setIsHeaderVisible] = useState<boolean>(true);
  const lastScrollY = useRef<number>(0);

  // Auto-hide header on scroll down, show on scroll up
  useEffect(() => {
    const handleScroll = (e?: Event) => {
      const target = e?.target as EventTarget | null;
      let currentScrollY = 0;
      if (!target || target === document || target === document.documentElement || target === window) {
        currentScrollY = window.scrollY || document.documentElement.scrollTop || 0;
      } else if (target && 'scrollTop' in target) {
        currentScrollY = (target as HTMLElement).scrollTop;
      }

      if (currentScrollY > lastScrollY.current && currentScrollY > 30) {
        setIsHeaderVisible(false);
      } else if (currentScrollY < lastScrollY.current - 5 || currentScrollY <= 20) {
        setIsHeaderVisible(true);
      }
      lastScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { capture: true, passive: true });
    return () => window.removeEventListener('scroll', handleScroll, { capture: true });
  }, []);

  // Custom navigation layout: 'global' or one of the stages
  const [activeLayer, setActiveLayer] = useState<FunnelStage | 'global'>('global');

  // Main view mode: central journey plus operational modules.
  const [mainView, setMainView] = useState<'canvas' | 'events' | 'crm' | 'studio' | 'digital_ai' | 'library' | 'sponsors' | 'metrics'>('events');
  const [advancedModuleView, setAdvancedModuleView] = useState<'canvas' | 'crm' | 'studio' | 'digital_ai' | 'library' | 'sponsors' | 'metrics' | null>(null);
  const [eventResumeArea, setEventResumeArea] = useState<'home' | 'event' | 'people' | 'content'>('home');
  const [crmReturnArea, setCrmReturnArea] = useState<'home' | 'event' | 'people' | 'content' | null>(null);

  useEffect(() => {
    if (mainView !== 'crm') setCrmReturnArea(null);
  }, [mainView]);

  useEffect(() => {
    if (advancedModuleView && mainView !== advancedModuleView) setAdvancedModuleView(null);
  }, [advancedModuleView, mainView]);

  // A project owns a portfolio of events. Legacy single-event data is migrated locally once.
  const [eventPortfolio, setEventPortfolio] = useState<EventPortfolioState>(() => {
    const savedPortfolio = localStorage.getItem('grosym_event_portfolio');
    if (savedPortfolio) {
      try {
        return normalizeEventPortfolioState(JSON.parse(savedPortfolio) as EventPortfolioState);
      } catch (error) {
        console.warn('Could not restore the local event portfolio:', error);
      }
    }
    const savedLegacyEvent = localStorage.getItem('grosym_event_operations');
    if (savedLegacyEvent) {
      try {
        const parsed = JSON.parse(savedLegacyEvent) as EventOperationsState;
        if (parsed?.dataSource !== 'demo') {
          const normalized = cleanEmptyEventConfiguration(normalizeEventOperationsState(parsed, createBlankEventOperationsState()));
          return createEventPortfolioState(normalized);
        }
      } catch (error) {
        console.warn('Could not migrate the local event workspace:', error);
      }
    }
    return createEventPortfolioState();
  });

  const eventOperations = useMemo(() => getActiveEvent(eventPortfolio), [eventPortfolio]);
  const setEventOperations = useCallback((nextEvent: EventOperationsState) => {
    setEventPortfolio((current) => replaceActiveEvent(current, nextEvent));
  }, []);
  const activeEventIndex = useMemo(() => Math.max(0, eventPortfolio.events.findIndex(
    (event) => event.event.id === eventPortfolio.activeEventId,
  )), [eventPortfolio]);
  const handleDeleteEvent = useCallback((eventId: string) => {
    const target = eventPortfolio.events.find((event) => event.event.id === eventId);
    if (!target) return;
    const index = eventPortfolio.events.indexOf(target);
    const label = target.event.name || `Oferta sin nombre ${index + 1}`;
    if (window.confirm(`¿Eliminar "${label}"? Esta acción quitará su configuración, personas y operaciones asociadas.`)) {
      setEventPortfolio((current) => removeEventFromPortfolio(current, eventId));
    }
  }, [eventPortfolio.events]);

  useEffect(() => {
    localStorage.setItem('grosym_event_portfolio', JSON.stringify(eventPortfolio));
  }, [eventPortfolio]);

  const [sponsorOperations, setSponsorOperations] = useState<SponsorOperationsState>(() => {
    const saved = localStorage.getItem('grosym_sponsor_operations_v2');
    if (saved) {
      try {
        const parsed = JSON.parse(saved) as SponsorOperationsState;
        return parsed?.dataSource === 'demo'
          ? EMPTY_SPONSOR_OPERATIONS
          : cleanSponsorDemoRecords(normalizeSponsorOperationsState({ ...EMPTY_SPONSOR_OPERATIONS, ...parsed, dataSource: 'provider' }, []));
      } catch (error) {
        console.warn('Could not restore Sponsor Operations:', error);
      }
    }
    return EMPTY_SPONSOR_OPERATIONS;
  });

  useEffect(() => {
    localStorage.setItem('grosym_sponsor_operations_v2', JSON.stringify(sponsorOperations));
  }, [sponsorOperations]);

  // Sponsors State
  const [sponsors, setSponsors] = useState<Sponsor[]>(() => {
    const saved = localStorage.getItem('grosym_sponsors_list');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          return parsed.filter((item) => !LEGACY_SPONSOR_SAMPLE_IDS.has(item?.id));
        }
      } catch (e) {}
    }
    return [];
  });

  useEffect(() => {
    localStorage.setItem('grosym_sponsors_list', JSON.stringify(sponsors));
  }, [sponsors]);

  const sponsorsForRelations = useMemo(() => {
    const related = [...sponsors];
    for (const brand of sponsorOperations.brands) {
      if (related.some(item => item.id === brand.id)) continue;
      related.push({
        id: brand.id,
        companyName: brand.brandName,
        contactName: sponsorOperations.contacts.find(item => item.brandId === brand.id)?.name || 'Sin contacto',
        contactEmail: sponsorOperations.contacts.find(item => item.brandId === brand.id)?.email || '',
        websiteUrl: brand.website,
        tier: 'community',
        amount: sponsorOperations.opportunities.filter(item => item.brandId === brand.id).reduce((sum, item) => sum + item.contractValue, 0),
        status: sponsorOperations.opportunities.some(item => item.brandId === brand.id && item.stage === 'active') ? 'confirmed' : 'prospect',
        notes: brand.notes,
        perks: [],
        assignedOwner: brand.owner,
        industry: brand.industry,
      });
    }
    return related;
  }, [sponsors, sponsorOperations]);

  const handleAddSponsor = (newSponsor: Sponsor) => {
    setSponsors(prev => [newSponsor, ...prev]);
  };

  const handleUpdateSponsor = (updated: Sponsor) => {
    setSponsors(prev => prev.map(s => s.id === updated.id ? updated : s));
  };

  const handleDeleteSponsor = (sponsorId: string) => {
    setSponsors(prev => prev.filter(s => s.id !== sponsorId));
  };

  // CRM Opportunities State
  const [opportunities, setOpportunities] = useState<CrmOpportunity[]>(() => {
    const saved = localStorage.getItem('grosym_crm_opportunities');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          return parsed.filter((item) => !LEGACY_CRM_SAMPLE_IDS.has(item?.id));
        }
      } catch (e) {}
    }
    return [];
  });

  useEffect(() => {
    localStorage.setItem('grosym_crm_opportunities', JSON.stringify(opportunities));
  }, [opportunities]);

  const handleAddOpportunity = (opp: CrmOpportunity) => {
    setOpportunities(prev => [opp, ...prev]);
  };

  const handleUpdateOpportunity = (opp: CrmOpportunity) => {
    setOpportunities(prev => prev.map(o => o.id === opp.id ? opp : o));
  };

  const handleDeleteOpportunity = (id: string) => {
    setOpportunities(prev => prev.filter(o => o.id !== id));
  };

  const handleImportOpportunities = (imported: CrmOpportunity[]) => {
    setOpportunities(prev => [...imported, ...prev]);
  };

  // Custom interface toggles - default sidebar to open on large displays, collapsed on small screens
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(() => {
    if (typeof window !== 'undefined' && window.innerWidth < 1024) {
      return false;
    }
    return true;
  });
  const [showAddAppDropdown, setShowAddAppDropdown] = useState<boolean>(false);
  const [addToolModalStage, setAddToolModalStage] = useState<FunnelStage | 'all' | null>(null);

  // Interaction & Multi-selection States
  const [selectedNodeIds, setSelectedNodeIds] = useState<string[]>([]);
  const [isMultiSelectMode, setIsMultiSelectMode] = useState<boolean>(false);
  const [draggedNodeId, setDraggedNodeId] = useState<string | null>(null);

  // Live Drag-to-Connect State (Drawing connection line from anchor points)
  const [connectingDrag, setConnectingDrag] = useState<{
    fromNodeId: string;
    fromPort: 'top' | 'bottom' | 'left' | 'right';
    startX: number;
    startY: number;
    currentX: number;
    currentY: number;
    hoveredTargetId: string | null;
    hasMoved: boolean;
  } | null>(null);
  const connectingDragRef = useRef<{
    fromNodeId: string;
    fromPort: 'top' | 'bottom' | 'left' | 'right';
    startX: number;
    startY: number;
    currentX: number;
    currentY: number;
    hoveredTargetId: string | null;
    hasMoved: boolean;
  } | null>(null);

  const selectedNodeId = selectedNodeIds.length > 0 ? selectedNodeIds[0] : null;

  // Marquee Selection Box State
  const [selectionBox, setSelectionBox] = useState<{ startX: number; startY: number; currentX: number; currentY: number } | null>(null);

  // Dynamic Stage Boundary Dividers State
  const DEFAULT_STAGE_DIVIDERS = [0, 11.11, 22.22, 33.33, 44.44, 55.55, 66.66, 77.77, 88.88, 100];
  const [stageDividers, setStageDividers] = useState<number[]>(DEFAULT_STAGE_DIVIDERS);
  const [draggedDividerIndex, setDraggedDividerIndex] = useState<number | null>(null);
  const dividerDragStartRef = useRef<{ clientY: number; startVal: number }>({ clientY: 0, startVal: 0 });

  // Locked Stages State (prevent moving and deleting)
  const [lockedStages, setLockedStages] = useState<FunnelStage[]>([]);
  const [openStageMenu, setOpenStageMenu] = useState<FunnelStage | null>(null);

  const toggleStageLock = (stage: FunnelStage) => {
    setLockedStages(prev =>
      prev.includes(stage) ? prev.filter(s => s !== stage) : [...prev, stage]
    );
  };

  // References for Dragging & Selection
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const canvasScrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [mobileCanvasStageIndex, setMobileCanvasStageIndex] = useState(0);
  const isDraggingRef = useRef<boolean>(false);
  const dragStartPosRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const dragStartPercentMapRef = useRef<Map<string, { x: number; y: number }>>(new Map());
  const justDraggedBoxRef = useRef<boolean>(false);
  const isMarqueeActiveRef = useRef<boolean>(false);

  const jumpToMobileCanvasStage = (requestedIndex: number) => {
    const lastPairStart = Math.max(0, STAGES_ORDER.length - 2);
    const nextIndex = Math.max(0, Math.min(lastPairStart, requestedIndex));
    const container = canvasScrollContainerRef.current;

    setMobileCanvasStageIndex(nextIndex);
    if (!container) return;

    container.scrollTo({
      top: nextIndex * (container.clientHeight / 2),
      behavior: 'smooth'
    });
  };

  const handleMobileCanvasScroll = () => {
    const container = canvasScrollContainerRef.current;
    if (!container || window.innerWidth >= 768) return;

    const visibleIndex = Math.max(
      0,
      Math.min(STAGES_ORDER.length - 2, Math.round(container.scrollTop / Math.max(1, container.clientHeight / 2)))
    );

    setMobileCanvasStageIndex(current => current === visibleIndex ? current : visibleIndex);
  };

  // --- HISTORY & CLIPBOARD FOR SHORTCUTS (Ctrl+Z, Ctrl+C, Ctrl+V, Ctrl+Y) ---
  const [undoStack, setUndoStack] = useState<Array<{ nodes: AppNode[]; links: AppLink[]; parameters: FunnelParameters }>>([]);
  const [redoStack, setRedoStack] = useState<Array<{ nodes: AppNode[]; links: AppLink[]; parameters: FunnelParameters }>>([]);
  const clipboardRef = useRef<{ nodes: AppNode[]; links: AppLink[] } | null>(null);

  const pushSnapshot = () => {
    setUndoStack(prev => [
      ...prev.slice(-25),
      {
        nodes: JSON.parse(JSON.stringify(nodes)),
        links: JSON.parse(JSON.stringify(links)),
        parameters: JSON.parse(JSON.stringify(parameters))
      }
    ]);
    setRedoStack([]);
  };

  const handleUndo = () => {
    if (undoStack.length === 0) return;
    const previousState = undoStack[undoStack.length - 1];

    setRedoStack(prev => [
      ...prev,
      {
        nodes: JSON.parse(JSON.stringify(nodes)),
        links: JSON.parse(JSON.stringify(links)),
        parameters: JSON.parse(JSON.stringify(parameters))
      }
    ]);

    setUndoStack(prev => prev.slice(0, prev.length - 1));

    setNodes(previousState.nodes);
    setLinks(previousState.links);
    setParameters(previousState.parameters);
    saveClientState(previousState.nodes, previousState.links, previousState.parameters, activeClientIndex);
  };

  const handleRedo = () => {
    if (redoStack.length === 0) return;
    const nextState = redoStack[redoStack.length - 1];

    setUndoStack(prev => [
      ...prev,
      {
        nodes: JSON.parse(JSON.stringify(nodes)),
        links: JSON.parse(JSON.stringify(links)),
        parameters: JSON.parse(JSON.stringify(parameters))
      }
    ]);

    setRedoStack(prev => prev.slice(0, prev.length - 1));

    setNodes(nextState.nodes);
    setLinks(nextState.links);
    setParameters(nextState.parameters);
    saveClientState(nextState.nodes, nextState.links, nextState.parameters, activeClientIndex);
  };

  const handleCopy = () => {
    if (selectedNodeIds.length === 0) return;
    const nodesToCopy = nodes.filter(n => selectedNodeIds.includes(n.id));
    if (nodesToCopy.length === 0) return;

    const linksToCopy = links.filter(
      l => selectedNodeIds.includes(l.fromId) && selectedNodeIds.includes(l.toId)
    );

    clipboardRef.current = {
      nodes: JSON.parse(JSON.stringify(nodesToCopy)),
      links: JSON.parse(JSON.stringify(linksToCopy))
    };
  };

  const handlePaste = () => {
    if (!clipboardRef.current || clipboardRef.current.nodes.length === 0) return;

    pushSnapshot();

    const { nodes: copiedNodes, links: copiedLinks } = clipboardRef.current;
    const idMap = new Map<string, string>();
    const newPastedNodes: AppNode[] = [];
    const newPastedNodeIds: string[] = [];

    const stages = STAGES_ORDER.map(s => s.stage);

    copiedNodes.forEach((copiedNode, index) => {
      const newId = `node-${Date.now()}-${index}-${Math.random().toString(36).substring(2, 6)}`;
      idMap.set(copiedNode.id, newId);
      newPastedNodeIds.push(newId);

      // Offset position slightly
      const offsetX = 3;
      const offsetY = 3;
      const newX = Math.min(92, Math.max(8, copiedNode.x + offsetX));
      let newY = Math.min(95, Math.max(2, copiedNode.y + offsetY));

      let newStage = copiedNode.stage;
      if (activeLayer !== 'global') {
        newStage = activeLayer;
        const stageIdx = stages.indexOf(activeLayer);
        const validIdx = stageIdx >= 0 ? stageIdx : 0;
        const startY = stageDividers[validIdx] || 0;
        const endY = stageDividers[validIdx + 1] || startY + 11.11;
        newY = startY + (endY - startY) / 2 + (index * 2) % 4;
      } else {
        for (let i = 0; i < stages.length; i++) {
          const minY = stageDividers[i];
          const maxY = stageDividers[i + 1];
          if (newY >= minY && newY <= maxY) {
            newStage = stages[i];
            break;
          }
        }
      }

      newPastedNodes.push({
        ...copiedNode,
        id: newId,
        stage: newStage,
        x: Number(newX.toFixed(1)),
        y: Number(newY.toFixed(1))
      });
    });

    const newPastedLinks: AppLink[] = [];
    copiedLinks.forEach((link, index) => {
      const newFromId = idMap.get(link.fromId);
      const newToId = idMap.get(link.toId);
      if (newFromId && newToId) {
        newPastedLinks.push({
          id: `link-pasted-${Date.now()}-${index}`,
          fromId: newFromId,
          toId: newToId
        });
      }
    });

    // Update clipboardRef to new pasted nodes so subsequent pastes shift further
    clipboardRef.current = {
      nodes: JSON.parse(JSON.stringify(newPastedNodes)),
      links: JSON.parse(JSON.stringify(newPastedLinks))
    };

    const updatedNodes = [...nodes, ...newPastedNodes];
    const updatedLinks = [...links, ...newPastedLinks];

    setNodes(updatedNodes);
    setLinks(updatedLinks);
    setSelectedNodeIds(newPastedNodeIds);
    saveClientState(updatedNodes, updatedLinks, parameters, activeClientIndex);
  };

  // Global Keyboard listener for shortcuts (Ctrl+Z, Ctrl+C, Ctrl+V, Ctrl+Y, Delete/Backspace)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const activeElem = document.activeElement as HTMLElement | null;
      if (
        activeElem &&
        (activeElem.tagName === 'INPUT' ||
          activeElem.tagName === 'TEXTAREA' ||
          activeElem.isContentEditable ||
          activeElem.closest('input') ||
          activeElem.closest('textarea'))
      ) {
        return;
      }

      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedNodeIds.length > 0 || selectedNodeId) {
          e.preventDefault();
          deleteSelectedNodes();
        }
        return;
      }

      const isCmdOrCtrl = e.metaKey || e.ctrlKey;
      if (!isCmdOrCtrl) return;

      const key = e.key.toLowerCase();

      if (key === 'z') {
        if (e.shiftKey) {
          e.preventDefault();
          handleRedo();
        } else {
          e.preventDefault();
          handleUndo();
        }
      } else if (key === 'y') {
        e.preventDefault();
        handleRedo();
      } else if (key === 'c') {
        e.preventDefault();
        handleCopy();
      } else if (key === 'v') {
        e.preventDefault();
        handlePaste();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [nodes, links, parameters, selectedNodeIds, selectedNodeId, lockedStages, undoStack, redoStack, activeClientIndex]);

  const handleAutoConnectNodes = () => {
    if (nodes.length < 2) return;
    pushSnapshot();
    const sorted = [...nodes].sort((a, b) => a.y - b.y);
    const newLinks: AppLink[] = [];

    for (let i = 0; i < sorted.length - 1; i++) {
      newLinks.push({
        id: `link-auto-${sorted[i].id}-${sorted[i+1].id}-${Date.now()}`,
        fromId: sorted[i].id,
        toId: sorted[i+1].id
      });
    }

    setLinks(newLinks);
    saveClientState(nodes, newLinks, parameters, activeClientIndex);
  };

  // --- AUTH & PERSONAL DB HANDLERS ---
  const handleLogin = (profile: UserProfile) => {
    const normEmail = profile.email.toLowerCase().trim();
    const userDbKey = `grosym_user_db_${normEmail}`;
    const savedUserDb = localStorage.getItem(userDbKey);

    // Check registered email registry
    let registeredList: string[] = [];
    const savedRegisteredUsersRaw = localStorage.getItem('grosym_registered_emails');
    if (savedRegisteredUsersRaw) {
      try {
        const parsed = JSON.parse(savedRegisteredUsersRaw);
        if (Array.isArray(parsed)) registeredList = parsed;
      } catch (e) {}
    }

    const isAlreadyRegistered = profile.isRegistered || registeredList.includes(normEmail) || !!savedUserDb;

    const updatedProfile: UserProfile = {
      ...profile,
      email: normEmail,
      isLoggedIn: true,
      isRegistered: true
    };

    setUserProfile(updatedProfile);
    localStorage.setItem('grosym_user_auth', JSON.stringify(updatedProfile));

    if (isAlreadyRegistered) {
      // REGISTERED USER: Do NOT onboard! Restore existing workspace directly.
      setShowOnboardingWizard(false);

      if (savedUserDb) {
        try {
          const parsed = JSON.parse(savedUserDb);
          const restoredNodes = Array.isArray(parsed.nodes) && parsed.nodes.length > 0 ? parsed.nodes : PRESET_FLOWS[0].nodes;
          const restoredLinks = parsed.links || PRESET_FLOWS[0].links;
          const restoredParams = parsed.parameters || PRESET_FLOWS[0].parameters;
          setNodes(restoredNodes);
          setLinks(restoredLinks);
          setParameters(restoredParams);
          saveClientState(restoredNodes, restoredLinks, restoredParams, 0, 'global', normEmail);
          return;
        } catch (e) {}
      }
      return;
    }

    // UNREGISTERED USER: Register email address and launch Onboarding Wizard
    if (!registeredList.includes(normEmail)) {
      registeredList.push(normEmail);
      localStorage.setItem('grosym_registered_emails', JSON.stringify(registeredList));
    }

    // Trigger Onboarding Wizard for new users
    setShowOnboardingWizard(true);
  };

  const handleOnboardingComplete = async (
    updatedProfile: UserProfile,
    generatedNodes: AppNode[],
    generatedLinks: AppLink[],
    generatedParams: FunnelParameters,
    customFlow: PresetFlow,
    answers: OnboardingAnswers
  ) => {
    const normEmail = updatedProfile.email.toLowerCase().trim();
    const finalProfile: UserProfile = {
      ...updatedProfile,
      email: normEmail,
      isLoggedIn: true,
      isRegistered: true,
      hasCompletedOnboarding: true
    };

    // Save into registered emails registry
    let registeredList: string[] = [];
    const savedRegisteredUsersRaw = localStorage.getItem('grosym_registered_emails');
    if (savedRegisteredUsersRaw) {
      try {
        const parsed = JSON.parse(savedRegisteredUsersRaw);
        if (Array.isArray(parsed)) registeredList = parsed;
      } catch (e) {}
    }
    if (!registeredList.includes(normEmail)) {
      registeredList.push(normEmail);
      localStorage.setItem('grosym_registered_emails', JSON.stringify(registeredList));
    }

    setUserProfile(finalProfile);
    localStorage.setItem('grosym_user_auth', JSON.stringify(finalProfile));

    setUserCustomFlow(customFlow);
    setNodes(generatedNodes);
    setLinks(generatedLinks);
    setParameters(generatedParams);
    setActiveClientIndex(1);
    setSelectedNodeIds([]);

    // Persist to Firestore & partitioned local cache
    await saveUserOnboardingData(finalProfile.uid, finalProfile.organizationId, answers, customFlow);
    saveClientState(generatedNodes, generatedLinks, generatedParams, 1, 'global', normEmail);

    setShowOnboardingWizard(false);
    setTimeout(() => {
      setShowProductTour(true);
    }, 400);
  };

  const handleAddNodeFromVoice = (stage: FunnelStage, name: string, iconName?: string) => {
    const stageIndex = STAGES_ORDER.findIndex(s => s.stage === stage);
    const targetStageIndex = stageIndex !== -1 ? stageIndex : 2;
    const minY = stageDividers[targetStageIndex] !== undefined ? stageDividers[targetStageIndex] : targetStageIndex * 11.11;
    const maxY = stageDividers[targetStageIndex + 1] !== undefined ? stageDividers[targetStageIndex + 1] : (targetStageIndex + 1) * 11.11;

    const existingNodesInStage = nodes.filter(n => n.stage === stage);
    const offsetCount = existingNodesInStage.length;

    const newX = Math.min(85, Math.max(15, 25 + (offsetCount * 18) % 60));
    const newY = minY + ((maxY - minY) * 0.4) + ((offsetCount % 3) * 2);

    const newNode: AppNode = {
      id: `voice-node-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
      name: name,
      stage: stage,
      x: newX,
      y: newY,
      iconName: iconName || 'Briefcase',
      funnelRolePurpose: 'Creado mediante Comando por Voz (Voice AI)',
      assignedPersonName: userProfile?.name || 'Equipo',
    };

    const updatedNodes = [...nodes, newNode];
    setNodes(updatedNodes);
    setSelectedNodeIds([newNode.id]);
    saveClientState(updatedNodes, links, parameters, activeClientIndex ?? 0, activeLayer, userProfile?.email);
  };

  const handleLogout = async () => {
    try {
      await logOutFromFirebase();
    } catch (_) {}
    localStorage.removeItem('grosym_user_auth');
    setUserProfile({
      uid: 'guest_user',
      email: 'invitado@grosym.com',
      name: 'Usuario Invitado',
      role: 'viewer',
      organizationId: 'org_demo',
      organizationName: 'Espacio Demo',
      activeProjectId: 'proj_demo',
      isLoggedIn: false
    });
    setAuthFlowState('signed_out');
    setShowLoginModal(true);
  };

  // --- FIREBASE AUTH REAL-TIME STATE LISTENER & HYDRATION ---
  useEffect(() => {
    const unsubscribe = subscribeToAuth(async (firebaseUser) => {
      if (!firebaseUser) {
        setAuthFlowState('signed_out');
        setIsHydrated(true);
        setCloudHydrationHealthy(false);
        return;
      }

      setIsHydrated(false);
      setCloudHydrationHealthy(false);
      setAuthFlowState('checking_access');
      try {
        const validation = await validateUserAccessAndMembership(
          firebaseUser.uid,
          firebaseUser.email || '',
          firebaseUser.displayName || 'Usuario',
          firebaseUser.photoURL
        );

        if (!validation.authorized || !validation.userProfile) {
          setAccessDeniedReason(validation.reason || 'Tu cuenta no tiene autorización o invitación activa.');
          setAuthFlowState('access_denied');
          setIsHydrated(true);
          setShowLoginModal(true);
          await logOutFromFirebase().catch(() => undefined);
          return;
        }

        const profile = validation.userProfile;
        setAccessDeniedReason('');
        setShowLoginModal(false);
        setUserProfile(profile);
        localStorage.setItem('grosym_user_auth', JSON.stringify(profile));

        const projects = validation.projects || [];
        if (projects.length === 0) {
          setAccessDeniedReason('La organización no tiene un proyecto accesible para esta cuenta.');
          setAuthFlowState('access_denied');
          setIsHydrated(true);
          return;
        }

        setCurrentProjects(projects);
        const projId = profile.activeProjectId || projects[0].id;
        setActiveProjectId(projId);
        setAuthFlowState('hydrating');

        const results = await Promise.all([
          loadProjectWorkspace(profile.uid, profile.organizationId, projId),
          loadProjectOpportunities(profile.uid, profile.organizationId, projId),
          loadProjectStudio(profile.uid, profile.organizationId, projId),
          loadProjectSponsors(profile.uid, profile.organizationId, projId),
          loadProjectSponsorOperations(profile.uid, profile.organizationId, projId),
          loadProjectActivations(profile.uid, profile.organizationId, projId),
          loadProjectAiApps(profile.uid, profile.organizationId, projId),
          loadProjectAiExecutionHistory(profile.uid, profile.organizationId, projId),
          loadProjectLibrary(profile.uid, profile.organizationId, projId),
          loadProjectEventPortfolio(profile.uid, profile.organizationId, projId),
        ]);
        const [workspaceRes, oppsRes, studioRes, sponsorsRes, sponsorOperationsRes, activationsRes, aiAppsRes, aiHistoryRes, libraryRes, eventOperationsRes] = results;

        if (workspaceRes.data) {
          const cloudNodes = (workspaceRes.data.nodes ?? []).filter((node) => !isLegacyGeneratedCanvasItem(node?.id));
          const cloudLinks = (workspaceRes.data.links ?? []).filter((link) => !isLegacyGeneratedCanvasItem(link?.id));
          const hadDemoCanvas = (workspaceRes.data.nodes ?? []).some((node) => isLegacyGeneratedCanvasItem(node?.id));
          setNodes(cloudNodes);
          setLinks(cloudLinks);
          setParameters(hadDemoCanvas ? DEFAULT_PARAMETERS : (workspaceRes.data.parameters ?? DEFAULT_PARAMETERS));
          setCustomToolTemplates(Array.isArray(workspaceRes.data.customToolTemplates)
            ? workspaceRes.data.customToolTemplates
            : []);
          if (workspaceRes.data.activeLayer) setActiveLayer(workspaceRes.data.activeLayer as FunnelStage | 'global');
        } else {
          setCustomToolTemplates([]);
        }
        if (oppsRes.data !== null) {
          setOpportunities(oppsRes.data.filter((item) => !LEGACY_CRM_SAMPLE_IDS.has(item.id)));
        }
        if (studioRes.data !== null) {
          setStudioItems(studioRes.data.filter((item) => !LEGACY_STUDIO_SAMPLE_IDS.has(item.id)));
        }
        if (sponsorsRes.data !== null) {
          setSponsors(sponsorsRes.data.filter((item) => !LEGACY_SPONSOR_SAMPLE_IDS.has(item.id)));
        }
        if (sponsorOperationsRes.data !== null) {
          const projectSponsors = (sponsorsRes.data || []).filter((item) => !LEGACY_SPONSOR_SAMPLE_IDS.has(item.id));
          setSponsorOperations(sponsorOperationsRes.data.dataSource === 'demo'
            ? EMPTY_SPONSOR_OPERATIONS
            : cleanSponsorDemoRecords(normalizeSponsorOperationsState(
              { ...EMPTY_SPONSOR_OPERATIONS, ...sponsorOperationsRes.data, dataSource: 'provider' },
              projectSponsors,
            )));
        } else {
          setSponsorOperations(EMPTY_SPONSOR_OPERATIONS);
        }
        if (activationsRes.data !== null) {
          const sampleIds = new Set(['act-registro-bienvenida', 'act-nutricion-recordatorio', 'act-asistencia-oferta']);
          setActivations(activationsRes.data.filter((item) => !sampleIds.has(item.id)));
        }
        if (aiAppsRes.data !== null) setDigitalAiAssets(aiAppsRes.data.filter((item) => item.dataSource !== 'demo'));
        if (aiHistoryRes.data !== null) setDigitalAiExecutionHistory(aiHistoryRes.data);
        if (libraryRes.data !== null) setLibraryItems(libraryRes.data);
        if (eventOperationsRes.data !== null) {
          const normalizedPortfolio = normalizeEventPortfolioState(eventOperationsRes.data);
          setEventPortfolio({
            ...normalizedPortfolio,
            events: normalizedPortfolio.events.map((event) => cleanEmptyEventConfiguration(event)),
          });
        } else if (eventOperationsRes.legacyEvent && eventOperationsRes.legacyEvent.dataSource !== 'demo') {
          const migratedEvent = cleanEmptyEventConfiguration(normalizeEventOperationsState(
            eventOperationsRes.legacyEvent,
            createBlankEventOperationsState(),
          ));
          setEventPortfolio(createEventPortfolioState(migratedEvent));
        } else {
          // Never leak the last project's local event into a newly selected project.
          setEventPortfolio(createEventPortfolioState());
        }

        setCloudHydrationHealthy(results.every(result => result.success));

        // Check legacy data for Owner (for explicit migration modal)
        if (profile.role === 'owner') {
          const legacy = checkLegacyLocalStorage();
          setLegacyMigrationData(legacy);
        }
      } catch (e) {
        console.warn('Could not complete cloud hydration:', e);
        setIsHydrated(true);
        setAuthFlowState('ready');
        return;
      }

      setIsHydrated(true);
      setAuthFlowState('ready');
    });

    return () => unsubscribe();
  }, []);

  // --- INITIALIZATION OF STAGE DIVIDERS ---
  useEffect(() => {
    const savedDividers = localStorage.getItem('grosym_stage_dividers_cascade');
    if (savedDividers) {
      try {
        const parsed = JSON.parse(savedDividers);
        if (Array.isArray(parsed) && parsed.length === 10) {
          setStageDividers(parsed);
        }
      } catch (e) {}
    }
  }, []);

  const cloudSaveTimersRef = useRef<Record<string, number>>({});
  const persistenceProjectId = activeProjectId || userProfile.activeProjectId || '';
  const canPersistToCloud = isHydrated && cloudHydrationHealthy && authFlowState === 'ready' &&
    userProfile.role !== 'viewer' && Boolean(persistenceProjectId);
  const handlePublishLanding = useCallback(async (offerState: EventOperationsState, slug: string) => {
    if (userProfile.role === 'viewer') {
      return { success: false, error: 'Tu perfil es de solo lectura y no puede publicar páginas.' };
    }
    if (!userProfile.organizationId || !persistenceProjectId) {
      return { success: false, error: 'Selecciona un proyecto antes de publicar.' };
    }
    try {
      const publicOffer = buildPublicOfferDocument(
        offerState,
        sponsorsForRelations,
        userProfile.organizationId,
        persistenceProjectId,
        slug,
      );
      const result = await publishPublicOffer(publicOffer);
      if (!result.success) {
        const code = result.error instanceof Error ? result.error.message : '';
        return {
          success: false,
          error: code === 'PUBLIC_OFFER_SLUG_TAKEN'
            ? 'Esa dirección ya está en uso. Prueba con otro nombre.'
            : 'No se pudo publicar. Revisa la conexión y vuelve a intentarlo.',
        };
      }
      return { success: true, localOnly: result.localOnly, url: getPublicOfferUrl(publicOffer.slug) };
    } catch (error) {
      console.warn('Could not prepare the public offer:', error);
      return { success: false, error: 'Completa el nombre y el borrador antes de publicar.' };
    }
  }, [persistenceProjectId, sponsorsForRelations, userProfile.organizationId, userProfile.role]);
  const handleUnpublishLanding = useCallback(async (offerState: EventOperationsState, slug: string) => {
    if (userProfile.role === 'viewer' || !userProfile.organizationId || !persistenceProjectId) {
      return { success: false, error: 'No tienes permisos para quitar esta página de línea.' };
    }
    const result = await unpublishPublicOffer(
      slug,
      userProfile.organizationId,
      persistenceProjectId,
      offerState.event.id,
    );
    return result.success
      ? { success: true, localOnly: result.localOnly }
      : { success: false, error: 'No se pudo quitar la página de línea.' };
  }, [persistenceProjectId, userProfile.organizationId, userProfile.role]);

  const scheduleCloudSave = (key: string, task: () => Promise<unknown>) => {
    const existingTimer = cloudSaveTimersRef.current[key];
    if (existingTimer) window.clearTimeout(existingTimer);
    setIsSyncing(true);
    cloudSaveTimersRef.current[key] = window.setTimeout(async () => {
      try {
        await task();
      } catch (error) {
        console.warn(`${key} cloud save failed:`, error);
      } finally {
        delete cloudSaveTimersRef.current[key];
        if (Object.keys(cloudSaveTimersRef.current).length === 0) setIsSyncing(false);
      }
    }, 700);
    return () => {
      const timer = cloudSaveTimersRef.current[key];
      if (timer) {
        window.clearTimeout(timer);
        delete cloudSaveTimersRef.current[key];
      }
    };
  };

  useEffect(() => {
    if (!canPersistToCloud) return;
    return scheduleCloudSave('workspace', () => saveProjectWorkspace(
      userProfile.uid, userProfile.organizationId, persistenceProjectId,
      { parameters, nodes, links, customToolTemplates, activeLayer },
    ));
  }, [nodes, links, parameters, customToolTemplates, activeLayer, canPersistToCloud, userProfile.uid, userProfile.organizationId, persistenceProjectId]);

  useEffect(() => {
    if (!canPersistToCloud) return;
    return scheduleCloudSave('crm', () => saveProjectOpportunities(
      userProfile.uid, userProfile.organizationId, persistenceProjectId, opportunities,
    ));
  }, [opportunities, canPersistToCloud, userProfile.uid, userProfile.organizationId, persistenceProjectId]);

  useEffect(() => {
    if (!canPersistToCloud) return;
    return scheduleCloudSave('studio', () => saveProjectStudio(
      userProfile.uid, userProfile.organizationId, persistenceProjectId, studioItems,
    ));
  }, [studioItems, canPersistToCloud, userProfile.uid, userProfile.organizationId, persistenceProjectId]);

  useEffect(() => {
    if (!canPersistToCloud) return;
    return scheduleCloudSave('sponsors', () => saveProjectSponsors(
      userProfile.uid, userProfile.organizationId, persistenceProjectId, sponsors,
    ));
  }, [sponsors, canPersistToCloud, userProfile.uid, userProfile.organizationId, persistenceProjectId]);

  useEffect(() => {
    if (!canPersistToCloud) return;
    return scheduleCloudSave('sponsor-operations-v2', () => saveProjectSponsorOperations(
      userProfile.uid, userProfile.organizationId, persistenceProjectId, sponsorOperations,
    ));
  }, [sponsorOperations, canPersistToCloud, userProfile.uid, userProfile.organizationId, persistenceProjectId]);

  useEffect(() => {
    if (!canPersistToCloud) return;
    return scheduleCloudSave('activations', () => saveProjectActivations(
      userProfile.uid, userProfile.organizationId, persistenceProjectId, activations,
    ));
  }, [activations, canPersistToCloud, userProfile.uid, userProfile.organizationId, persistenceProjectId]);

  useEffect(() => {
    if (!canPersistToCloud) return;
    return scheduleCloudSave('ai-apps', () => saveProjectAiApps(
      userProfile.uid, userProfile.organizationId, persistenceProjectId, digitalAiAssets,
    ));
  }, [digitalAiAssets, canPersistToCloud, userProfile.uid, userProfile.organizationId, persistenceProjectId]);

  useEffect(() => {
    if (!canPersistToCloud) return;
    return scheduleCloudSave('ai-history', () => saveProjectAiExecutionHistory(
      userProfile.uid, userProfile.organizationId, persistenceProjectId, digitalAiExecutionHistory,
    ));
  }, [digitalAiExecutionHistory, canPersistToCloud, userProfile.uid, userProfile.organizationId, persistenceProjectId]);

  useEffect(() => {
    if (!canPersistToCloud) return;
    return scheduleCloudSave('library', () => saveProjectLibrary(
      userProfile.uid, userProfile.organizationId, persistenceProjectId, libraryItems,
    ));
  }, [libraryItems, canPersistToCloud, userProfile.uid, userProfile.organizationId, persistenceProjectId]);

  useEffect(() => {
    if (!canPersistToCloud) return;
    return scheduleCloudSave('event-portfolio', () => saveProjectEventPortfolio(
      userProfile.uid, userProfile.organizationId, persistenceProjectId, eventPortfolio,
    ));
  }, [eventPortfolio, canPersistToCloud, userProfile.uid, userProfile.organizationId, persistenceProjectId]);

  // Legacy Migration Trigger (Owner only, manual)
  const handleTriggerLegacyMigration = async () => {
    if (!userProfile || userProfile.role !== 'owner' || !activeProjectId) return;
    setMigrationRunning(true);
    try {
      const result = await executeLegacyMigration(userProfile.uid, userProfile.organizationId, activeProjectId, userProfile.role);
      setMigrationFeedback(result);
      // Refresh legacy status
      const updatedLegacy = checkLegacyLocalStorage();
      setLegacyMigrationData(updatedLegacy);
    } catch (err: any) {
      console.error('Migration failed:', err);
    } finally {
      setMigrationRunning(false);
    }
  };

  // Local partitioned cache helper
  const saveClientState = (
    newNodes: AppNode[],
    newLinks: AppLink[],
    newParams: FunnelParameters,
    _clientIdx: number = activeClientIndex,
    layer: FunnelStage | 'global' = activeLayer,
    _overrideEmail?: string,
    toolTemplates: ToolTemplate[] = customToolTemplates,
  ) => {
    if (userProfile?.role === 'viewer') return;
    if (userProfile?.uid && userProfile?.organizationId && activeProjectId) {
      const cacheKey = `grosym_cache_${userProfile.uid}_${userProfile.organizationId}_${activeProjectId}_workspace`;
      try {
        localStorage.setItem(cacheKey, JSON.stringify({
          nodes: newNodes,
          links: newLinks,
          parameters: newParams,
          customToolTemplates: toolTemplates,
          activeLayer: layer,
          updatedAt: new Date().toISOString()
        }));
      } catch (_) {}
    }
  };

  const loadClient = (index: number) => {
    if (index === 1 && !userCustomFlow) {
      setShowOnboardingWizard(true);
      return;
    }
    const client = allAvailableFlows[index] || PRESET_FLOWS[0];
    setNodes(client.nodes);
    setLinks(client.links);
    setParameters(client.parameters);
    setActiveClientIndex(index);
    setSelectedNodeIds([]);
    setActiveLayer('global');
    setShowAddAppDropdown(false);
  };

  // --- CALCULATE REALISTIC KPIs ---
  const metrics = useMemo(() => {
    const traffic = parameters.traffic;

    // Impact of Education: highly certified creator boosts conversion rates & authority
    const eduMultiplier = 1 + (parameters.educationalHours - 200) / 1200;

    // Impact of Self Clarity (Mind): high clarity reduces friction by up to 30%
    const clarityMultiplier = parameters.selfClarityScore / 80;

    const registrations = Math.round(traffic * (parameters.registrationRate / 100));

    // Conversiones generadas in nurturing stage
    const conversations = Math.round(registrations * (parameters.chatEngagementRate / 100));

    // Live show-up
    const attendance = Math.round(registrations * (parameters.showUpRate / 100));

    // Sales Conversions (multiplied by clarity and education factors!)
    const rawConversionRate = parameters.conversionRate * clarityMultiplier * eduMultiplier;
    const effectiveConversionRate = Math.min(50, Math.max(0.5, rawConversionRate));
    const conversions = Math.round(attendance * (effectiveConversionRate / 100));

    // Retention VIP rate is directly modulated by deliveryQuality score!
    const effectiveRetentionRate = parameters.retentionRate * (parameters.deliveryQuality / 100);
    const retentions = Math.round(conversions * (effectiveRetentionRate / 100));

    // Revenue calculation
    const initialSalesRevenue = conversions * parameters.baseTicketPrice;
    const recurringRevenue = retentions * parameters.baseTicketPrice * 1.5;
    const totalRevenue = initialSalesRevenue + recurringRevenue;

    return {
      traffic,
      registrations,
      conversations,
      attendance,
      conversions,
      retentions,
      initialSalesRevenue,
      recurringRevenue,
      totalRevenue
    };
  }, [parameters]);

  // --- NODE QUICK-ADD EVENT PROXY ---
  const handleAddToolProxy = (
    app: Pick<ToolTemplate, 'name' | 'iconName' | 'stage'>,
    overrideStage?: FunnelStage,
    toolTemplatesOverride?: ToolTemplate[],
  ) => {
    pushSnapshot();
    const targetStage: FunnelStage = overrideStage ||
      (addToolModalStage && addToolModalStage !== 'all' ? (addToolModalStage as FunnelStage) : undefined) ||
      (activeLayer !== 'global' ? (activeLayer as FunnelStage) : undefined) ||
      app.stage ||
      'attraction';

    const countInStage = nodes.filter(n => n.stage === targetStage).length;

    // Default positioning in middle of current stage horizontal slice
    const defaultX = 25 + (countInStage * 15) % 55;

    const stagesList = STAGES_ORDER.map(s => s.stage);
    const stageIdx = stagesList.indexOf(targetStage);
    const validIdx = stageIdx >= 0 ? stageIdx : 0;
    const startY = stageDividers[validIdx] || 0;
    const endY = stageDividers[validIdx + 1] || startY + 11.11;
    const stageCenterY = startY + (endY - startY) / 2;
    const defaultY = Math.max(startY + 1.2, Math.min(endY - 1.2, stageCenterY - 1 + (countInStage * 2) % 4));

    const newNode: AppNode = {
      id: 'node-' + Date.now(),
      name: app.name,
      iconName: app.iconName,
      stage: targetStage,
      x: defaultX,
      y: Number(defaultY.toFixed(1)),
      customLabel: ''
    };

    const newNodes = [...nodes, newNode];
    setNodes(newNodes);
    setSelectedNodeIds([newNode.id]);
    setShowAddAppDropdown(false);
    saveClientState(newNodes, links, parameters, activeClientIndex, activeLayer, undefined, toolTemplatesOverride);
  };

  const handleCreateCustomTool = (template: ToolTemplate, destinationStage: FunnelStage) => {
    const updatedTemplates = [template, ...customToolTemplates];
    setCustomToolTemplates(updatedTemplates);
    handleAddToolProxy(template, destinationStage, updatedTemplates);
  };

  const handleDeleteCustomTool = (templateId: string) => {
    const updatedTemplates = customToolTemplates.filter((template) => template.id !== templateId);
    setCustomToolTemplates(updatedTemplates);
    saveClientState(nodes, links, parameters, activeClientIndex, activeLayer, undefined, updatedTemplates);
  };

  const handleUpdateNodeFromModal = (updatedNode: AppNode) => {
    const previousNode = nodes.find((node) => node.id === updatedNode.id);
    const positionedNode = previousNode && previousNode.stage !== updatedNode.stage
      ? moveNodeToStage(updatedNode, updatedNode.stage, FUNNEL_STAGE_ORDER, stageDividers)
      : updatedNode;
    const updatedNodes = nodes.map((node) => node.id === positionedNode.id ? positionedNode : node);
    setNodes(updatedNodes);
    saveClientState(updatedNodes, links, parameters, activeClientIndex);
  };

  // --- DRAGGING & CONNECTING EVENTS ---
  const startDrag = (e: React.MouseEvent | React.TouchEvent, node: AppNode) => {
    if (lockedStages.includes(node.stage)) return;
    const target = e.target as HTMLElement;
    if (target.closest('.no-drag') || target.closest('.anchor-point')) return;

    const isMultiKey = e.shiftKey || e.metaKey || e.ctrlKey;
    let activeIds = selectedNodeIds;

    if (!selectedNodeIds.includes(node.id) && !isMultiKey && !isMultiSelectMode) {
      activeIds = [node.id];
      setSelectedNodeIds([node.id]);
    } else if (!selectedNodeIds.includes(node.id) && (isMultiKey || isMultiSelectMode)) {
      activeIds = [...selectedNodeIds, node.id];
      setSelectedNodeIds(activeIds);
    }

    setDraggedNodeId(node.id);
    isDraggingRef.current = false;

    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

    dragStartPosRef.current = { x: clientX, y: clientY };

    // Record initial positions for all nodes being dragged
    const map = new Map<string, { x: number; y: number }>();
    nodes.forEach(n => {
      if (activeIds.includes(n.id)) {
        map.set(n.id, { x: n.x, y: n.y });
      }
    });
    dragStartPercentMapRef.current = map;
  };

  useEffect(() => {
    const handleMove = (e: MouseEvent | TouchEvent) => {
      if (!draggedNodeId || !canvasRef.current) return;

      const rect = canvasRef.current.getBoundingClientRect();
      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

      const dx = clientX - dragStartPosRef.current.x;
      const dy = clientY - dragStartPosRef.current.y;

      if (Math.hypot(dx, dy) > 4) {
        isDraggingRef.current = true;
      }

      const stage9BottomBase = stageDividers[9] !== undefined ? stageDividers[9] : 100;
      const dxPercent = (dx / rect.width) * 100;

      let dyBase: number;
      if (activeLayer !== 'global') {
        const sIdx = STAGES_ORDER.findIndex(s => s.stage === activeLayer);
        const stageHeight = (sIdx >= 0 && stageDividers[sIdx + 1] !== undefined)
          ? (stageDividers[sIdx + 1] - stageDividers[sIdx])
          : 11.11;
        dyBase = (dy / rect.height) * stageHeight;
      } else {
        dyBase = (dy / rect.height) * stage9BottomBase;
      }

      const updatedNodes = nodes.map(n => {
        const startPos = dragStartPercentMapRef.current.get(n.id);
        if (startPos) {
          if (lockedStages.includes(n.stage)) return n;

          const newX = Math.max(10, Math.min(90, startPos.x + dxPercent));
          const proposedY = startPos.y + dyBase;

          // In the global canvas, crossing a divider moves the tool to that stage.
          // The focused stage view remains constrained because it only renders one stage.
          const proposedStage = activeLayer === 'global'
            ? getStageAtY(proposedY, FUNNEL_STAGE_ORDER, stageDividers)
            : n.stage;
          const targetStage = lockedStages.includes(proposedStage) && proposedStage !== n.stage
            ? n.stage
            : proposedStage;
          const clampedY = clampYToStage(proposedY, targetStage, FUNNEL_STAGE_ORDER, stageDividers);

          return {
            ...n,
            x: Number(newX.toFixed(1)),
            y: Number(clampedY.toFixed(1)),
            stage: targetStage
          };
        }
        return n;
      });

      setNodes(updatedNodes);
      saveClientState(updatedNodes, links, parameters, activeClientIndex);
    };

    const handleEnd = () => {
      if (draggedNodeId) {
        if (!isDraggingRef.current) {
          // If simple click on node card without drag, select and open development drawer
          handleCenterNodeClick(draggedNodeId);
        }
        setDraggedNodeId(null);
      }
    };

    if (draggedNodeId) {
      window.addEventListener('mousemove', handleMove);
      window.addEventListener('mouseup', handleEnd);
      window.addEventListener('touchmove', handleMove, { passive: false });
      window.addEventListener('touchend', handleEnd);
    }

    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleEnd);
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('touchend', handleEnd);
    };
  }, [draggedNodeId, nodes, links, parameters, activeClientIndex, stageDividers, activeLayer, lockedStages]);

  // --- INTERACTIVE STAGE DIVIDER DRAGGING ---
  const handleDividerMouseDown = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    pushSnapshot();
    setDraggedDividerIndex(index);
    dividerDragStartRef.current = {
      clientY: e.clientY,
      startVal: stageDividers[index]
    };
  };

  useEffect(() => {
    if (draggedDividerIndex === null) return;

    const handleDividerMove = (e: MouseEvent) => {
      if (!canvasRef.current) return;
      const rect = canvasRef.current.getBoundingClientRect();
      const dy = e.clientY - dividerDragStartRef.current.clientY;
      const stage9BottomBase = stageDividers[9] !== undefined ? stageDividers[9] : 100;
      const dyBase = (dy / rect.height) * stage9BottomBase;

      const k = draggedDividerIndex;
      const startVal = dividerDragStartRef.current.startVal;
      let newPos = startVal + dyBase;

      const MIN_STAGE = 5.0;
      if (k < 8) {
        const minAllowed = stageDividers[k - 1] + MIN_STAGE;
        const maxAllowed = stageDividers[k + 1] - MIN_STAGE;
        newPos = Math.max(minAllowed, Math.min(maxAllowed, newPos));
      } else {
        const minAllowed = stageDividers[7] + MIN_STAGE;
        newPos = Math.max(minAllowed, newPos);
      }

      const updatedDividers = [...stageDividers];
      updatedDividers[k] = Number(newPos.toFixed(2));

      // Calculate required minimum height for stage 8 (Retención VIP) so nodes fit and canvas shrinks/expands dynamically
      const stage8Key = STAGES_ORDER[8]?.stage || 'retention';
      const stage8Nodes = nodes.filter(n => n.stage === stage8Key);
      const maxNodeYInStage8 = stage8Nodes.length > 0 ? Math.max(...stage8Nodes.map(n => n.y)) : 0;
      const defaultStage8Height = 11.11;
      const stage8NeededHeight = stage8Nodes.length > 0
        ? Math.max(defaultStage8Height, maxNodeYInStage8 - updatedDividers[8] + 2.5)
        : defaultStage8Height;

      // Bottom of stage 8 moves with divider 8 so stage 8 stays at its default/needed size and canvas ends cleanly
      updatedDividers[9] = Number((updatedDividers[8] + stage8NeededHeight).toFixed(2));

      setStageDividers(updatedDividers);

      // Auto-push nodes that fall outside their updated stage bounds
      const adjustedNodes = nodes.map(n => {
        const sIdx = STAGES_ORDER.findIndex(s => s.stage === n.stage);
        if (sIdx < 0) return n;
        const minY = updatedDividers[sIdx] + 1.2;
        const maxY = updatedDividers[sIdx + 1] - 1.2;
        if (n.y < minY || n.y > maxY) {
          return {
            ...n,
            y: Number(Math.max(minY, Math.min(maxY, n.y)).toFixed(1))
          };
        }
        return n;
      });

      setNodes(adjustedNodes);
      localStorage.setItem('grosym_stage_dividers_cascade', JSON.stringify(updatedDividers));
      saveClientState(adjustedNodes, links, parameters, activeClientIndex);
    };

    const handleDividerUp = () => {
      setDraggedDividerIndex(null);
    };

    window.addEventListener('mousemove', handleDividerMove);
    window.addEventListener('mouseup', handleDividerUp);
    return () => {
      window.removeEventListener('mousemove', handleDividerMove);
      window.removeEventListener('mouseup', handleDividerUp);
    };
  }, [draggedDividerIndex, stageDividers, nodes, links, parameters, activeClientIndex]);

  const handleResetStageDividers = () => {
    pushSnapshot();
    const DEFAULT_STAGE_DIVIDERS = [0, 11.11, 22.22, 33.33, 44.44, 55.55, 66.66, 77.77, 88.88, 100];
    setStageDividers(DEFAULT_STAGE_DIVIDERS);
    localStorage.setItem('grosym_stage_dividers_cascade', JSON.stringify(DEFAULT_STAGE_DIVIDERS));
  };

  // --- MARQUEE SELECTION (BOX DRAG TO SELECT MULTIPLE NODES) ---
  const handleCanvasMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target.closest('.anchor-point') || target.closest('button') || target.closest('.no-drag') || target.closest('.group')) return;

    if (canvasRef.current) {
      const rect = canvasRef.current.getBoundingClientRect();
      const currentScale = canvasScale || 1;
      const startX = (e.clientX - rect.left) / currentScale;
      const startY = (e.clientY - rect.top) / currentScale;

      isMarqueeActiveRef.current = false;

      setSelectionBox({
        startX,
        startY,
        currentX: startX,
        currentY: startY,
      });
    }
  };

  useEffect(() => {
    if (!selectionBox) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!canvasRef.current) return;
      const rect = canvasRef.current.getBoundingClientRect();
      const currentScale = canvasScale || 1;
      const currentX = (e.clientX - rect.left) / currentScale;
      const currentY = (e.clientY - rect.top) / currentScale;

      const dist = Math.hypot(currentX - selectionBox.startX, currentY - selectionBox.startY);
      if (dist > 5) {
        isMarqueeActiveRef.current = true;
      }

      setSelectionBox(prev => prev ? { ...prev, currentX, currentY } : null);

      if (isMarqueeActiveRef.current) {
        const minPxX = Math.min(selectionBox.startX, currentX);
        const maxPxX = Math.max(selectionBox.startX, currentX);
        const minPxY = Math.min(selectionBox.startY, currentY);
        const maxPxY = Math.max(selectionBox.startY, currentY);

        const internalWidth = canvasRef.current.offsetWidth || (rect.width / currentScale);
        const internalHeight = canvasRef.current.offsetHeight || (rect.height / currentScale);

        // Calculate node overlapping bounds
        const inBoxIds = nodes
          .filter(n => {
            const stage9BottomBase = stageDividers[9] !== undefined ? stageDividers[9] : 100;
            const nodePxX = (n.x / 100) * internalWidth;
            const nodePxY = (n.y / stage9BottomBase) * internalHeight;
            const radius = 35; // approximate node hit boundary in px
            const nodeMinX = nodePxX - radius;
            const nodeMaxX = nodePxX + radius;
            const nodeMinY = nodePxY - radius;
            const nodeMaxY = nodePxY + radius;

            return nodeMinX <= maxPxX && nodeMaxX >= minPxX && nodeMinY <= maxPxY && nodeMaxY >= minPxY;
          })
          .map(n => n.id);

        if (e.shiftKey || e.metaKey || e.ctrlKey) {
          const merged = Array.from(new Set([...selectedNodeIds, ...inBoxIds]));
          setSelectedNodeIds(merged);
        } else {
          setSelectedNodeIds(inBoxIds);
        }
      }
    };

    const handleMouseUp = () => {
      if (isMarqueeActiveRef.current) {
        justDraggedBoxRef.current = true;
        setTimeout(() => {
          justDraggedBoxRef.current = false;
          isMarqueeActiveRef.current = false;
        }, 150);
      }
      setSelectionBox(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [selectionBox, nodes, selectedNodeIds, canvasScale, stageDividers]);

  // --- MOUSE WHEEL ZOOM (CTRL + WHEEL / PINCH TO ZOOM CANVASES) ---
  useEffect(() => {
    const container = canvasScrollContainerRef.current;
    if (!container) return;

    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault(); // Prevent full page browser zoom

        const zoomDelta = e.deltaY < 0 ? 0.08 : -0.08;
        setCanvasScale(prev => {
          const next = Math.max(0.35, Math.min(1.5, +(prev + zoomDelta).toFixed(2)));
          localStorage.setItem('grosym_canvas_scale', next.toString());
          return next;
        });
      }
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, []);

  // --- DRAG-TO-CONNECT LIVE LINE LOGIC ---
  const handleAnchorStart = (
    e: React.MouseEvent | React.TouchEvent,
    node: AppNode,
    port: 'top' | 'bottom' | 'left' | 'right'
  ) => {
    e.stopPropagation();
    if ('cancelable' in e && e.cancelable) {
      e.preventDefault();
    }

    const stage9BottomBase = stageDividers[9] !== undefined ? stageDividers[9] : 100;
    const nodeYPercent = (node.y / stage9BottomBase) * 100;
    let startX = node.x;
    let startY = nodeYPercent;

    if (port === 'top') startY -= 2.0;
    else if (port === 'bottom') startY += 2.0;
    else if (port === 'left') startX -= 2.2;
    else if (port === 'right') startX += 2.2;

    const newDragState = {
      fromNodeId: node.id,
      fromPort: port,
      startX,
      startY,
      currentX: startX,
      currentY: startY,
      hoveredTargetId: null,
      hasMoved: false
    };

    connectingDragRef.current = newDragState;
    setConnectingDrag(newDragState);
  };

  useEffect(() => {
    if (!connectingDrag) return;

    const handlePointerMove = (e: MouseEvent | TouchEvent) => {
      if (!canvasRef.current || !connectingDragRef.current) return;

      if ('touches' in e && e.cancelable) {
        e.preventDefault();
      }

      const rect = canvasRef.current.getBoundingClientRect();
      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

      const currentScale = canvasScale || 1;
      const pxX = (clientX - rect.left) / currentScale;
      const pxY = (clientY - rect.top) / currentScale;

      const internalWidth = canvasRef.current.offsetWidth || (rect.width / currentScale);
      const internalHeight = canvasRef.current.offsetHeight || (rect.height / currentScale);

      const currentX = Math.max(0, Math.min(100, (pxX / internalWidth) * 100));
      const currentY = Math.max(0, Math.min(100, (pxY / internalHeight) * 100));

      const stage9BottomBase = stageDividers[9] !== undefined ? stageDividers[9] : 100;
      const fromId = connectingDragRef.current.fromNodeId;
      let hoveredId: string | null = null;

      for (const n of nodes) {
        if (n.id === fromId) continue;
        const nYPercent = (n.y / stage9BottomBase) * 100;
        const dist = Math.hypot(n.x - currentX, nYPercent - currentY);
        if (dist < 6.5) {
          hoveredId = n.id;
          break;
        }
      }

      const updated = {
        ...connectingDragRef.current,
        currentX,
        currentY,
        hoveredTargetId: hoveredId,
        hasMoved: true
      };
      connectingDragRef.current = updated;
      setConnectingDrag(updated);
    };

    const handlePointerEnd = (e: MouseEvent | TouchEvent) => {
      const current = connectingDragRef.current;
      if (current) {
        const fromId = current.fromNodeId;
        let targetId = current.hoveredTargetId;

        // If targetId not caught yet, check end touch coordinates
        if (!targetId && 'changedTouches' in e && canvasRef.current) {
          const rect = canvasRef.current.getBoundingClientRect();
          const clientX = e.changedTouches[0].clientX;
          const clientY = e.changedTouches[0].clientY;
          const currentScale = canvasScale || 1;
          const pxX = (clientX - rect.left) / currentScale;
          const pxY = (clientY - rect.top) / currentScale;
          const internalWidth = canvasRef.current.offsetWidth || (rect.width / currentScale);
          const internalHeight = canvasRef.current.offsetHeight || (rect.height / currentScale);
          const endX = (pxX / internalWidth) * 100;
          const endY = (pxY / internalHeight) * 100;
          const stage9BottomBase = stageDividers[9] !== undefined ? stageDividers[9] : 100;

          for (const n of nodes) {
            if (n.id === fromId) continue;
            const nYPercent = (n.y / stage9BottomBase) * 100;
            if (Math.hypot(n.x - endX, nYPercent - endY) < 7.5) {
              targetId = n.id;
              break;
            }
          }
        }

        if (targetId && targetId !== fromId) {
          // Connect or toggle connection
          pushSnapshot();
          const alreadyLinked = links.some(
            l => (l.fromId === fromId && l.toId === targetId) ||
                 (l.fromId === targetId && l.toId === fromId)
          );

          if (alreadyLinked) {
            const newLinks = links.filter(
              l => !( (l.fromId === fromId && l.toId === targetId) || (l.fromId === targetId && l.toId === fromId) )
            );
            setLinks(newLinks);
            saveClientState(nodes, newLinks, parameters, activeClientIndex);
          } else {
            const newLink: AppLink = {
              id: 'link-' + Date.now(),
              fromId,
              toId: targetId
            };
            const newLinks = [...links, newLink];
            setLinks(newLinks);
            saveClientState(nodes, newLinks, parameters, activeClientIndex);
          }
        } else if (!current.hasMoved) {
          // Simple tap on green anchor without dragging: do not open development drawer
          if (selectedNodeIds.length === 1 && selectedNodeIds[0] !== fromId) {
            const firstId = selectedNodeIds[0];
            pushSnapshot();
            const alreadyLinked = links.some(
              l => (l.fromId === firstId && l.toId === fromId) ||
                   (l.fromId === fromId && l.toId === firstId)
            );
            if (alreadyLinked) {
              const newLinks = links.filter(
                l => !( (l.fromId === firstId && l.toId === fromId) || (l.fromId === fromId && l.toId === firstId) )
              );
              setLinks(newLinks);
              saveClientState(nodes, newLinks, parameters, activeClientIndex);
            } else {
              const newLink: AppLink = {
                id: 'link-' + Date.now(),
                fromId: firstId,
                toId: fromId
              };
              const newLinks = [...links, newLink];
              setLinks(newLinks);
              saveClientState(nodes, newLinks, parameters, activeClientIndex);
            }
            setSelectedNodeIds([]);
          } else {
            setSelectedNodeIds([fromId]);
          }
        }
      }

      connectingDragRef.current = null;
      setConnectingDrag(null);
    };

    window.addEventListener('mousemove', handlePointerMove);
    window.addEventListener('mouseup', handlePointerEnd);
    window.addEventListener('touchmove', handlePointerMove, { passive: false });
    window.addEventListener('touchend', handlePointerEnd);

    return () => {
      window.removeEventListener('mousemove', handlePointerMove);
      window.removeEventListener('mouseup', handlePointerEnd);
      window.removeEventListener('touchmove', handlePointerMove);
      window.removeEventListener('touchend', handlePointerEnd);
    };
  }, [connectingDrag, nodes, links, parameters, activeClientIndex, canvasScale, stageDividers, selectedNodeIds]);

  // --- CENTER NODE CLICK (OPENS DEVELOPMENT DRAWER & SELECTS NODE) ---
  const handleCenterNodeClick = (nodeId: string, isMultiKey: boolean = false) => {
    if (isMultiSelectMode || isMultiKey) {
      if (selectedNodeIds.includes(nodeId)) {
        if (selectedNodeIds.length > 1) {
          setSelectedNodeIds(selectedNodeIds.filter(id => id !== nodeId));
        }
      } else {
        setSelectedNodeIds([...selectedNodeIds, nodeId]);
      }
      setSidebarOpen(true);
      return;
    }

    // Direct click in center of node: select and open development tab
    setSelectedNodeIds([nodeId]);
    setSidebarOpen(true);
  };

  // --- LINKING & NODE REMOVAL ---
  const handleNodeClick = (nodeId: string, isMultiKey: boolean = false) => {
    handleCenterNodeClick(nodeId, isMultiKey);
  };

  const handleUpdateNode = (nodeId: string, updates: Partial<AppNode>) => {
    pushSnapshot();
    const updatedNodes = nodes.map(n => n.id === nodeId ? { ...n, ...updates } : n);
    setNodes(updatedNodes);
    saveClientState(updatedNodes, links, parameters, activeClientIndex);
  };

  const deleteNode = (nodeId: string) => {
    const targetNode = nodes.find(n => n.id === nodeId);
    if (targetNode && lockedStages.includes(targetNode.stage)) {
      return;
    }
    pushSnapshot();
    const newNodes = nodes.filter(n => n.id !== nodeId);
    const newLinks = links.filter(l => l.fromId !== nodeId && l.toId !== nodeId);

    setNodes(newNodes);
    setLinks(newLinks);
    setSelectedNodeIds(selectedNodeIds.filter(id => id !== nodeId));
    saveClientState(newNodes, newLinks, parameters, activeClientIndex);
  };

  const deleteSelectedNodes = () => {
    const idsToDelete = Array.from(new Set([
      ...selectedNodeIds,
      ...(selectedNodeId ? [selectedNodeId] : [])
    ]));
    if (idsToDelete.length === 0) return;

    const unlockableIds = idsToDelete.filter(id => {
      const node = nodes.find(n => n.id === id);
      return node ? !lockedStages.includes(node.stage) : true;
    });
    if (unlockableIds.length === 0) return;

    pushSnapshot();
    const newNodes = nodes.filter(n => !unlockableIds.includes(n.id));
    const newLinks = links.filter(l => !unlockableIds.includes(l.fromId) && !unlockableIds.includes(l.toId));
    setNodes(newNodes);
    setLinks(newLinks);
    setSelectedNodeIds(selectedNodeIds.filter(id => !unlockableIds.includes(id)));
    saveClientState(newNodes, newLinks, parameters, activeClientIndex);
  };

  const chainConnectSelectedNodes = () => {
    if (selectedNodeIds.length < 2) return;
    pushSnapshot();
    const newLinks = [...links];
    for (let i = 0; i < selectedNodeIds.length - 1; i++) {
      const fromId = selectedNodeIds[i];
      const toId = selectedNodeIds[i + 1];
      const exists = newLinks.some(
        l => (l.fromId === fromId && l.toId === toId) || (l.fromId === toId && l.toId === fromId)
      );
      if (!exists) {
        newLinks.push({
          id: `link-chain-${Date.now()}-${i}`,
          fromId,
          toId,
        });
      }
    }
    setLinks(newLinks);
    saveClientState(nodes, newLinks, parameters, activeClientIndex);
  };

  // --- ALIGNMENT & EVEN SPACING (DISTRIBUTE) FUNCTIONS FOR MULTI-SELECTION ---
  const distributeSelectedNodesHorizontal = () => {
    if (selectedNodeIds.length < 2) return;
    const selectedNodes = nodes.filter(n => selectedNodeIds.includes(n.id));
    if (selectedNodes.length < 2) return;

    pushSnapshot();
    if (selectedNodes.length === 2) {
      // With 2 nodes, align Y to average Y for neat horizontal pairing
      const avgY = (selectedNodes[0].y + selectedNodes[1].y) / 2;
      const updatedNodes = nodes.map(n => {
        if (selectedNodeIds.includes(n.id)) {
          return { ...n, y: Number(avgY.toFixed(2)) };
        }
        return n;
      });
      setNodes(updatedNodes);
      saveClientState(updatedNodes, links, parameters, activeClientIndex);
      return;
    }

    const sorted = [...selectedNodes].sort((a, b) => a.x - b.x);
    const minX = sorted[0].x;
    const maxX = sorted[sorted.length - 1].x;
    let step = (maxX - minX) / (sorted.length - 1);
    let startX = minX;

    if (Math.abs(maxX - minX) < 1) {
      step = 10;
      startX = Math.max(5, minX - ((sorted.length - 1) * step) / 2);
    }

    const posMap = new Map<string, number>();
    sorted.forEach((n, idx) => {
      posMap.set(n.id, startX + idx * step);
    });

    const updatedNodes = nodes.map(n => {
      if (posMap.has(n.id)) {
        return { ...n, x: Number(posMap.get(n.id)!.toFixed(2)) };
      }
      return n;
    });

    setNodes(updatedNodes);
    saveClientState(updatedNodes, links, parameters, activeClientIndex);
  };

  const distributeSelectedNodesVertical = () => {
    if (selectedNodeIds.length < 2) return;
    const selectedNodes = nodes.filter(n => selectedNodeIds.includes(n.id));
    if (selectedNodes.length < 2) return;

    pushSnapshot();
    if (selectedNodes.length === 2) {
      // With 2 nodes, align X to average X for neat vertical pairing
      const avgX = (selectedNodes[0].x + selectedNodes[1].x) / 2;
      const updatedNodes = nodes.map(n => {
        if (selectedNodeIds.includes(n.id)) {
          return { ...n, x: Number(avgX.toFixed(2)) };
        }
        return n;
      });
      setNodes(updatedNodes);
      saveClientState(updatedNodes, links, parameters, activeClientIndex);
      return;
    }

    const sorted = [...selectedNodes].sort((a, b) => a.y - b.y);
    const minY = sorted[0].y;
    const maxY = sorted[sorted.length - 1].y;
    let step = (maxY - minY) / (sorted.length - 1);
    let startY = minY;

    if (Math.abs(maxY - minY) < 1) {
      step = 10;
      startY = Math.max(5, minY - ((sorted.length - 1) * step) / 2);
    }

    const posMap = new Map<string, number>();
    sorted.forEach((n, idx) => {
      posMap.set(n.id, startY + idx * step);
    });

    const updatedNodes = nodes.map(n => {
      if (posMap.has(n.id)) {
        return { ...n, y: Number(posMap.get(n.id)!.toFixed(2)) };
      }
      return n;
    });

    setNodes(updatedNodes);
    saveClientState(updatedNodes, links, parameters, activeClientIndex);
  };

  const alignSelectedNodesVerticalCenter = () => {
    if (selectedNodeIds.length < 2) return;
    const selectedNodes = nodes.filter(n => selectedNodeIds.includes(n.id));
    if (selectedNodes.length < 2) return;

    pushSnapshot();
    const avgY = selectedNodes.reduce((acc, n) => acc + n.y, 0) / selectedNodes.length;
    const updatedNodes = nodes.map(n => {
      if (selectedNodeIds.includes(n.id)) {
        return { ...n, y: Number(avgY.toFixed(2)) };
      }
      return n;
    });

    setNodes(updatedNodes);
    saveClientState(updatedNodes, links, parameters, activeClientIndex);
  };

  const alignSelectedNodesHorizontalCenter = () => {
    if (selectedNodeIds.length < 2) return;
    const selectedNodes = nodes.filter(n => selectedNodeIds.includes(n.id));
    if (selectedNodes.length < 2) return;

    pushSnapshot();
    const avgX = selectedNodes.reduce((acc, n) => acc + n.x, 0) / selectedNodes.length;
    const updatedNodes = nodes.map(n => {
      if (selectedNodeIds.includes(n.id)) {
        return { ...n, x: Number(avgX.toFixed(2)) };
      }
      return n;
    });

    setNodes(updatedNodes);
    saveClientState(updatedNodes, links, parameters, activeClientIndex);
  };

  const deleteLink = (linkId: string) => {
    const link = links.find(l => l.id === linkId);
    if (link) {
      const fn = nodes.find(n => n.id === link.fromId);
      const tn = nodes.find(n => n.id === link.toId);
      if ((fn && lockedStages.includes(fn.stage)) || (tn && lockedStages.includes(tn.stage))) {
        return;
      }
    }
    pushSnapshot();
    const newLinks = links.filter(l => l.id !== linkId);
    setLinks(newLinks);
    saveClientState(nodes, newLinks, parameters, activeClientIndex);
  };

  const connectSelectedToTarget = (targetId: string) => {
    if (selectedNodeIds.length === 0) return;
    pushSnapshot();
    const newLinks = [...links];
    let createdCount = 0;
    selectedNodeIds.forEach(fromId => {
      if (fromId !== targetId) {
        const exists = newLinks.some(l => l.fromId === fromId && l.toId === targetId);
        if (!exists) {
          newLinks.push({
            id: 'link-' + Date.now() + '-' + Math.random().toString(36).substr(2, 4),
            fromId,
            toId: targetId
          });
          createdCount++;
        }
      }
    });
    if (createdCount > 0) {
      setLinks(newLinks);
      saveClientState(nodes, newLinks, parameters, activeClientIndex);
    }
  };

  const handleParamChange = (key: keyof FunnelParameters, value: number) => {
    const updatedParams = {
      ...parameters,
      [key]: value
    };
    setParameters(updatedParams);
    saveClientState(nodes, links, updatedParams, activeClientIndex);
  };

  const handleRenameNode = (nodeId: string, newName: string) => {
    pushSnapshot();
    const updatedNodes = nodes.map(n => {
      if (n.id === nodeId) {
        return { ...n, name: newName };
      }
      return n;
    });
    setNodes(updatedNodes);
    saveClientState(updatedNodes, links, parameters, activeClientIndex);
  };

  const handleClear = () => {
    if (window.confirm('¿Borrar todas las herramientas y conexiones de este flujo?')) {
      pushSnapshot();
      setNodes([]);
      setLinks([]);
      setSelectedNodeIds([]);
      saveClientState([], [], parameters, activeClientIndex);
    }
  };

  const handleLayerSelect = (layer: FunnelStage | 'global') => {
    setActiveLayer(layer);
    setSelectedNodeIds([]);
    saveClientState(nodes, links, parameters, activeClientIndex, layer);
  };

  const getStageAppsCount = (stage: FunnelStage) => {
    return nodes.filter(n => n.stage === stage).length;
  };

  // --- BRAND STYLE MAP FOR REALISTIC ICONS ---
  const getBrandStyle = (name: string, iconName: string) => {
    const norm = name.toLowerCase();

    // Personal matches
    if (iconName === 'Compass' || norm.includes('claridad') || norm.includes('coaching')) {
      return { bg: 'bg-gradient-to-tr from-rose-500 to-rose-600', shadow: 'shadow-[0_4px_12px_rgba(244,63,94,0.35)]' };
    }
    if (iconName === 'Heart' || norm.includes('meditación') || norm.includes('ética') || norm.includes('bienestar')) {
      return { bg: 'bg-gradient-to-tr from-pink-500 to-rose-500', shadow: 'shadow-[0_4px_12px_rgba(236,72,153,0.35)]' };
    }
    if (iconName === 'Smile' || norm.includes('hábitos') || norm.includes('imagen')) {
      return { bg: 'bg-gradient-to-tr from-amber-400 to-rose-500', shadow: 'shadow-[0_4px_12px_rgba(244,63,94,0.3)]' };
    }
    if (iconName === 'Activity' || norm.includes('disciplina') || norm.includes('psicoterapia')) {
      return { bg: 'bg-gradient-to-tr from-red-500 to-orange-600', shadow: 'shadow-[0_4px_12px_rgba(239,68,68,0.3)]' };
    }

    // Education matches
    if (iconName === 'BookOpen' || norm.includes('curso') || norm.includes('lectura')) {
      return { bg: 'bg-gradient-to-tr from-blue-500 to-indigo-600', shadow: 'shadow-[0_4px_12px_rgba(59,130,246,0.35)]' };
    }
    if (iconName === 'Award' || norm.includes('certificación') || norm.includes('mastery')) {
      return { bg: 'bg-gradient-to-tr from-indigo-500 to-cyan-500', shadow: 'shadow-[0_4px_12px_rgba(99,102,241,0.35)]' };
    }
    if (iconName === 'GraduationCap' || norm.includes('taller') || norm.includes('especialidad')) {
      return { bg: 'bg-gradient-to-tr from-blue-600 to-sky-500', shadow: 'shadow-[0_4px_12px_rgba(56,189,248,0.3)]' };
    }

    // Offer matches
    if (iconName === 'Briefcase' || norm.includes('sponsor') || norm.includes('consultoría')) {
      return { bg: 'bg-gradient-to-tr from-violet-600 to-fuchsia-700', shadow: 'shadow-[0_4px_12px_rgba(139,92,246,0.35)]' };
    }
    if (iconName === 'Hammer' || norm.includes('taller intensivo')) {
      return { bg: 'bg-gradient-to-tr from-indigo-600 to-violet-700', shadow: 'shadow-[0_4px_12px_rgba(79,70,229,0.35)]' };
    }
    if (iconName === 'Sparkles' || norm.includes('tratamiento') || norm.includes(' premium')) {
      return { bg: 'bg-gradient-to-tr from-purple-500 to-pink-500', shadow: 'shadow-[0_4px_12px_rgba(168,85,247,0.35)]' };
    }

    // Attraction / Registration matches
    if (norm.includes('linkedin')) {
      return { bg: 'bg-gradient-to-tr from-[#0a66c2] to-[#3b82f6]', shadow: 'shadow-[0_4px_12px_rgba(10,102,194,0.35)]' };
    }
    if (norm.includes('meta') || norm.includes('facebook') || norm.includes('instagram') || norm.includes('insta')) {
      return { bg: 'bg-gradient-to-tr from-[#ee2a7b] via-[#FD5001] to-[#6228d7]', shadow: 'shadow-[0_4px_12px_rgba(253,80,1,0.35)]' };
    }
    if (norm.includes('google') || norm.includes('search')) {
      return { bg: 'bg-gradient-to-tr from-[#4285f4] via-[#ea4335] to-[#fbbc05]', shadow: 'shadow-[0_4px_12px_rgba(234,67,53,0.35)]' };
    }
    if (norm.includes('landing') || norm.includes('web')) {
      return { bg: 'bg-gradient-to-tr from-[#00C57C] to-[#05e391]', shadow: 'shadow-[0_4px_12px_rgba(0,197,124,0.35)]' };
    }
    if (norm.includes('calendly') || norm.includes(' citas') || norm.includes('agenda')) {
      return { bg: 'bg-gradient-to-tr from-[#006bff] to-[#0051bf]', shadow: 'shadow-[0_4px_12px_rgba(0,107,255,0.35)]' };
    }

    // Nurturing
    if (norm.includes('whatsapp') || norm.includes('comunidad') || norm.includes('grupo') || norm.includes('bot')) {
      return { bg: 'bg-gradient-to-tr from-[#25d366] to-[#128c7e]', shadow: 'shadow-[0_4px_12px_rgba(37,211,102,0.35)]' };
    }
    if (norm.includes('email') || norm.includes('mail') || norm.includes('secuencia')) {
      return { bg: 'bg-gradient-to-tr from-[#ea4335] to-[#c5221f]', shadow: 'shadow-[0_4px_12px_rgba(234,67,53,0.35)]' };
    }

    // Live Streaming
    if (norm.includes('zoom')) {
      return { bg: 'bg-gradient-to-tr from-[#2d8cff] to-[#1272e6]', shadow: 'shadow-[0_4px_12px_rgba(45,140,255,0.35)]' };
    }
    if (norm.includes('youtube') || norm.includes('live') || norm.includes('telemedicina') || norm.includes('sala')) {
      return { bg: 'bg-gradient-to-tr from-[#ff0000] to-[#b91c1c]', shadow: 'shadow-[0_4px_12px_rgba(239,68,68,0.35)]' };
    }

    // Retention / Continuous VIP matches
    if (iconName === 'Crown' || norm.includes('círculo') || norm.includes('vip')) {
      return { bg: 'bg-gradient-to-tr from-amber-400 to-yellow-600', shadow: 'shadow-[0_4px_12px_rgba(245,158,11,0.35)]' };
    }
    if (norm.includes('recurrente') || norm.includes('fidelización') || norm.includes('recurring')) {
      return { bg: 'bg-gradient-to-tr from-teal-500 to-emerald-600', shadow: 'shadow-[0_4px_12px_rgba(13,148,136,0.35)]' };
    }

    // Delivery / Fulfillment matches (NEW!)
    if (norm.includes('lms') || norm.includes('alumnos') || norm.includes('proyecto') || norm.includes('entrega')) {
      return { bg: 'bg-gradient-to-tr from-teal-500 to-teal-700', shadow: 'shadow-[0_4px_12px_rgba(13,148,136,0.35)]' };
    }

    // Fallback default
    return {
      bg: 'bg-gradient-to-tr from-[#00C57C] to-[#FD5001]',
      shadow: 'shadow-[0_4px_12px_rgba(0,197,124,0.3)]'
    };
  };

  // --- FILTERED DATA FOR CURRENT VIEW ---
  const selectedNode = useMemo(() => {
    return nodes.find(n => n.id === selectedNodeId);
  }, [nodes, selectedNodeId]);

  const activeClientName = clientNames[activeClientIndex] || allAvailableFlows[activeClientIndex]?.name || 'Estrategia';
  const activeClientDesc = allAvailableFlows[activeClientIndex]?.description || '';

  // Get active stage metadata
  const activeStageMetadata = useMemo(() => {
    if (activeLayer === 'global') return null;
    return STAGES_ORDER.find(item => item.stage === activeLayer) || null;
  }, [activeLayer]);

  // Stage navigation logic (Previous / Next deep dive stage)
  const handleNavigateStage = (direction: 'prev' | 'next') => {
    const stagesList: FunnelStage[] = [
      'personal', 'education', 'offer', 'attraction',
      'registration', 'nurturing', 'attendance', 'retention', 'delivery'
    ];
    let currentIdx = stagesList.indexOf(activeLayer as FunnelStage);
    if (currentIdx === -1) return;

    if (direction === 'prev') {
      currentIdx = currentIdx === 0 ? stagesList.length - 1 : currentIdx - 1;
    } else {
      currentIdx = currentIdx === stagesList.length - 1 ? 0 : currentIdx + 1;
    }

    handleLayerSelect(stagesList[currentIdx]);
  };

  const isViewer = !isSystemAdmin && userProfile?.role === 'viewer';
  const isOwner = isSystemAdmin || userProfile?.role === 'owner';
  const isEventExperience = mainView === 'events' && canAccessEvents;
  const isSimplifiedModuleHome = mainView !== 'events' && advancedModuleView !== mainView;

  // --- STRICT ACCESS SECURITY GATING (/flow) ---
  if (authFlowState === 'checking_auth' || authFlowState === 'checking_access' || authFlowState === 'hydrating') {
    return (
      <div className="h-screen w-screen bg-slate-950 flex flex-col items-center justify-center text-white space-y-4">
        <div className="w-12 h-12 rounded-2xl bg-emerald-500 text-slate-950 flex items-center justify-center font-black text-xl shadow-lg animate-pulse">
          G
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-400 font-medium">
          <Loader2 size={16} className="animate-spin text-emerald-400" />
          <span>Verificando credenciales y permisos de acceso a Grosym Flow...</span>
        </div>
      </div>
    );
  }

  if (authFlowState === 'signed_out' || !userProfile?.isLoggedIn) {
    return (
      <div className="h-screen w-screen bg-slate-950 flex items-center justify-center p-4">
        <LoginModal
          isOpen={true}
          accessDeniedReason={accessDeniedReason}
        />
      </div>
    );
  }

  if (authFlowState === 'access_denied' || !canAccessFlow) {
    return (
      <div className="h-screen w-screen bg-slate-950 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-slate-900 border border-slate-800 rounded-3xl p-8 shadow-2xl text-center flex flex-col items-center gap-5 text-white animate-scaleUp">
          <div className="w-16 h-16 rounded-2xl bg-rose-500/10 border border-rose-500/30 text-rose-400 flex items-center justify-center shadow-inner">
            <Lock size={28} />
          </div>

          <div className="space-y-1.5">
            <span className="text-[10px] font-black uppercase tracking-widest text-rose-400 bg-rose-500/10 px-2.5 py-1 rounded-full border border-rose-500/20">
              Acceso Restringido
            </span>
            <h2 className="text-xl font-black text-white mt-2">Módulo Grosym Flow No Asignado</h2>
            <p className="text-xs text-slate-400 font-medium leading-relaxed">
              {accessDeniedReason || `Tu cuenta (${userProfile.email}) no tiene asignado el permiso de acceso a Grosym Flow en el Panel de Administración.`}
            </p>
          </div>

          <div className="w-full pt-2 flex flex-col gap-2.5">
            <a
              href={`https://wa.me/5491123456789?text=Hola,%20solicito%20acceso%20al%20m%C3%B3dulo%20Grosym%20Flow%20para%20mi%20cuenta:%20${encodeURIComponent(userProfile.email)}`}
              target="_blank"
              rel="noreferrer"
              className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black py-3 px-5 rounded-xl text-xs transition-all shadow-md flex items-center justify-center gap-2 cursor-pointer"
            >
              Solicitar Activación a un Administrador
            </a>

            <button
              onClick={handleLogout}
              className="w-full bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold py-2.5 px-4 rounded-xl text-xs transition-colors cursor-pointer"
            >
              Cerrar Sesión / Cambiar Cuenta
            </button>

            <a
              href="/"
              className="text-[11px] text-slate-500 hover:text-emerald-400 font-bold transition-colors mt-1"
            >
              &larr; Volver al Portal Principal
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[100dvh] w-screen overflow-hidden bg-slate-50 flex flex-row font-sans text-slate-800 antialiased select-none">

      {/* FLOATING UNHIDE UI BUTTON FOR MOBILE */}
      {isMobileUiHidden && (
        <button
          onClick={() => setIsMobileUiHidden(false)}
          className="fixed top-3 right-3 z-50 bg-slate-900/90 hover:bg-slate-950 text-white px-3 py-1.5 rounded-full text-[10px] font-extrabold shadow-xl border border-slate-700 flex items-center gap-1.5 backdrop-blur-md cursor-pointer animate-fadeIn"
          title="Mostrar Interfaz Completa"
        >
          <Eye size={13} className="text-emerald-400" />
          <span>Mostrar UI</span>
        </button>
      )}

      {/* FLOATING MENU TOGGLE (ALWAYS VISIBLE & FLOATING WHEN SIDEBAR IS CLOSED) */}
      {!sidebarOpen && !isEventExperience && (
        <button
          type="button"
          onClick={() => setSidebarOpen(true)}
          className={`fixed top-10 md:top-3.5 left-2.5 md:left-3.5 z-40 w-11 h-11 md:w-10 md:h-10 rounded-2xl bg-white/95 hover:bg-white text-slate-800 hover:text-slate-950 border border-slate-200/90 shadow-md hover:shadow-lg items-center justify-center cursor-pointer transition-all duration-200 backdrop-blur-md hover:scale-105 active:scale-95 ${mainView === 'canvas' && activeLayer === 'global' ? 'hidden md:flex' : 'flex'}`}
          title="Abrir Menú / Panel Lateral"
          aria-label="Abrir Menú"
        >
          <Menu size={18} className="text-slate-800 stroke-[2.5]" />
        </button>
      )}

      {/* LEFT COLUMN: FULL-HEIGHT ASIDE SIDEBAR */}
      {sidebarOpen && !isEventExperience && (
        <React.Suspense fallback={<aside className="w-[280px] h-screen bg-slate-950 border-r border-slate-800" />}>
        <SidebarControls
          mainView={mainView}
          onSelectMainView={(v) => {
            setAdvancedModuleView(null);
            setCrmReturnArea(null);
            setMainView(v);
            if (v === 'canvas') {
              setActiveLayer('global');
            }
          }}
          opportunitiesCount={opportunities.filter(o => o.status === 'open').length}
          attendeesCount={eventOperations.attendees.length}
          totalNodesCount={nodes.length}
          sponsorsCount={sponsorOperations.brands.length}
          parameters={parameters}
          onParamChange={handleParamChange}
          metrics={metrics}
          activeClientName={activeClientName}
          activeClientDesc={activeClientDesc}
          onClear={handleClear}
          nodesCountByStage={getStageAppsCount}
          sidebarOpen={sidebarOpen}
          onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
          selectedNode={selectedNode}
          selectedNodeIds={selectedNodeIds}
          allNodes={nodes}
          links={links}
          teamMembers={teamMembers}
          onUpdateNode={handleUpdateNode}
          onDeleteNode={deleteNode}
          onDeleteLink={deleteLink}
          onCloseSelectedNode={() => setSelectedNodeIds([])}
          onDeleteSelectedNodes={deleteSelectedNodes}
          onChainConnectSelectedNodes={chainConnectSelectedNodes}
          onClearSelection={() => setSelectedNodeIds([])}
          onDistributeHorizontal={distributeSelectedNodesHorizontal}
          onDistributeVertical={distributeSelectedNodesVertical}
          onAlignHorizontalCenter={alignSelectedNodesVerticalCenter}
          onAlignVerticalCenter={alignSelectedNodesHorizontalCenter}
          onConnectSelectedToTarget={connectSelectedToTarget}
          onOpenToolModal={(node) => setConfigureNodeModal(node)}
          // Top Sidebar Branding & Strategy props
          isSyncing={isSyncing}
          activeClientIndex={activeEventIndex}
          clientNames={eventPortfolio.events.map((event, index) => event.event.name || `Oferta sin nombre ${index + 1}`)}
          onSelectClient={(val) => {
            if (val === 'NEW_CLIENT') {
              setEventPortfolio((current) => addEventToPortfolio(current));
              setEventResumeArea('event');
              setAdvancedModuleView(null);
              setMainView('events');
            } else {
              const target = eventPortfolio.events[val];
              if (target) setEventPortfolio((current) => selectEventInPortfolio(current, target.event.id));
              setEventResumeArea('home');
              setAdvancedModuleView(null);
              setMainView('events');
            }
          }}
          onDeleteClient={(index) => {
            const target = eventPortfolio.events[index];
            if (target) handleDeleteEvent(target.event.id);
          }}
          userProfile={userProfile}
          onOpenUserProfile={() => setShowUserProfileModal(true)}
          onOpenGoogleSheets={() => setShowGoogleSheetsModal(true)}
          onOpenFirebase={() => setShowFirebaseModal(true)}
          onOpenTeam={() => setShowTeamModal(true)}
          onOpenTour={() => setShowProductTour(true)}
          onLogout={handleLogout}
          voiceInlineStatus={voiceInlineStatus}
        />
        </React.Suspense>
      )}

      {/* RIGHT COLUMN: MAIN WORKSPACE & CANVAS */}
      <div className="flex-1 h-[100dvh] flex flex-col overflow-hidden min-w-0 bg-slate-50 relative">

        {/* Legacy migration remains available, but does not interrupt the guided event shell. */}
        {!isEventExperience && isOwner && legacyMigrationData.hasLegacyData && !migrationFeedback && (
          <div className="bg-teal-50 border-b border-teal-200 px-4 py-2 flex items-center justify-between text-xs text-teal-950 shrink-0">
            <div className="flex items-center gap-2">
              <span className="bg-teal-600 text-white font-bold text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider">
                Migración
              </span>
              <span className="font-medium text-teal-900">
                Se detectaron datos locales anteriores ({legacyMigrationData.detectedItems.customClientsCount} clientes, {legacyMigrationData.detectedItems.opportunitiesCount} oportunidades).
              </span>
            </div>
            <button
              onClick={handleTriggerLegacyMigration}
              disabled={migrationRunning}
              className="bg-teal-700 hover:bg-teal-800 text-white font-bold text-[11px] px-3 py-1 rounded-lg transition-colors cursor-pointer flex items-center gap-1.5"
            >
              {migrationRunning ? <Loader2 size={12} className="animate-spin" /> : <Database size={12} />}
              <span>{migrationRunning ? 'Migrando...' : 'Migrar a este Proyecto'}</span>
            </button>
          </div>
        )}

        {!isEventExperience && migrationFeedback && (
          <div className="bg-emerald-50 border-b border-emerald-200 px-4 py-1.5 flex items-center justify-between text-xs text-emerald-950 shrink-0">
            <span className="font-medium">
              ✅ Migración completada: {migrationFeedback.countImported} elementos importados a este proyecto. (Backup local creado).
            </span>
            <button
              onClick={() => setMigrationFeedback(null)}
              className="text-emerald-800 hover:text-emerald-950 font-bold text-[11px] px-2 py-0.5"
            >
              Entendido
            </button>
          </div>
        )}

        {/* TOP WORKSPACE SUB-BAR (Stage Navigation & Zoom Controls) */}
        {mainView !== 'crm' && !isEventExperience && !isSimplifiedModuleHome && (
        <div className={`bg-white px-2 sm:px-3 py-1.5 sm:py-2 items-center justify-between gap-2 shrink-0 border-b border-slate-150 shadow-2xs z-30 transition-all min-h-11 ${mainView === 'canvas' ? 'hidden' : 'flex'} ${!sidebarOpen ? 'pl-16' : ''}`}>
          <div className="flex items-center gap-2 flex-wrap">
            {mainView === 'canvas' && advancedModuleView === 'canvas' && (
              <button type="button" onClick={() => setAdvancedModuleView(null)} className="px-2.5 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-[11px] font-black flex items-center gap-1.5 border border-slate-200">
                <ChevronLeft size={13} /> Vista simple
              </button>
            )}
            {mainView !== 'canvas' && (
              <div className="flex items-center gap-1.5 text-xs text-slate-500 font-bold">
                <button
                  type="button"
                  onClick={() => {
                    setAdvancedModuleView(null);
                    setMainView('canvas');
                    setActiveLayer('global');
                  }}
                  className="px-2.5 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 hover:text-slate-950 text-[11px] font-black flex items-center gap-1.5 cursor-pointer transition-all border border-slate-200"
                  title="Volver al Lienzo Principal"
                >
                  <Layers size={13} className="text-emerald-600" />
                  <span>Lienzo Principal</span>
                </button>
                <span>/</span>
                <span className="px-2.5 py-1 rounded-lg bg-slate-900 text-white text-[10px] font-black uppercase tracking-wider flex items-center gap-1.5 shadow-2xs">
                  {mainView === 'crm' ? 'Pipeline CRM' :
                   mainView === 'studio' ? 'Content Studio' :
                   mainView === 'digital_ai' ? 'Digital AI Apps' :
                   mainView === 'sponsors' ? 'Sponsors & Patrocinios' :
                   mainView === 'events' ? 'Operación comercial' : 'Métricas & ROI'}
                </span>
              </div>
            )}

            {mainView === 'canvas' && activeLayer !== 'global' && (
              <div className="flex items-center gap-1.5 text-xs text-slate-500 font-bold">
                <button
                  type="button"
                  onClick={() => {
                    setActiveLayer('global');
                    setMainView('canvas');
                  }}
                  className="px-2.5 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 hover:text-slate-950 text-[11px] font-black flex items-center gap-1.5 cursor-pointer transition-all border border-slate-200"
                  title="Volver al Lienzo Principal"
                >
                  <Layers size={13} className="text-emerald-600" />
                  <span>Lienzo Principal</span>
                </button>
                <span>/</span>
                <span className="px-2.5 py-1 rounded-lg bg-slate-900 text-white text-[10px] font-black uppercase tracking-wider flex items-center gap-1.5 shadow-2xs">
                  <span style={{ color: STAGES_ORDER.find(s => s.stage === activeLayer)?.color }}>
                    {STAGE_ICONS[activeLayer]}
                  </span>
                  {STAGES_ORDER.find(s => s.stage === activeLayer)?.label}
                </span>
              </div>
            )}
          </div>

          {/* Zoom Controls & Voice status at the top right */}
          <div className="flex items-center gap-2">
            {voiceInlineStatus !== 'idle' && (
              <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-50 border border-emerald-300 text-emerald-950 font-black text-xs">
                {voiceInlineStatus === 'listening' && <Mic size={12} className="text-emerald-600 animate-pulse" />}
                {voiceInlineStatus === 'loading' && <Loader2 size={12} className="text-emerald-600 animate-spin" />}
                {voiceInlineStatus === 'completed' && <Check size={12} className="text-emerald-600" />}
                <span className="text-[10px] font-bold text-emerald-900 capitalize">{voiceInlineStatus}</span>
              </div>
            )}

            {mainView === 'canvas' && (
            <div className="hidden md:flex items-center gap-1 bg-slate-50 border border-slate-200 rounded-xl p-0.5 text-xs shadow-3xs">
              <button
                type="button"
                onClick={() => handleStepCanvasScale(-0.15)}
                className="w-9 h-9 sm:w-auto sm:h-auto sm:p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-200/70 rounded-lg cursor-pointer transition-colors flex items-center justify-center"
                title="Alejar y expandir lienzo (Zoom -)"
                aria-label="Alejar lienzo"
              >
                <ZoomOut size={13} />
              </button>

              <button
                type="button"
                onClick={handleToggleCanvasScale}
                className="hidden sm:block px-2 py-0.5 text-[10px] font-black font-mono text-slate-700 hover:text-slate-950 hover:bg-slate-200/70 rounded-md cursor-pointer transition-all border border-slate-200/60 bg-white"
                title="Alternar entre escala 100% y vista panorámica expandida (65%)"
              >
                {Math.round(canvasScale * 100)}%
              </button>

              <button
                type="button"
                onClick={() => handleStepCanvasScale(0.15)}
                className="w-9 h-9 sm:w-auto sm:h-auto sm:p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-200/70 rounded-lg cursor-pointer transition-colors flex items-center justify-center"
                title="Acercar lienzo (Zoom +)"
                aria-label="Acercar lienzo"
              >
                <ZoomIn size={13} />
              </button>

              <div className="hidden sm:block w-px h-3.5 bg-slate-200 my-auto mx-0.5" />

              <button
                type="button"
                onClick={handleToggleCanvasScale}
                className={`w-9 h-9 sm:w-auto sm:h-auto sm:p-1.5 rounded-lg cursor-pointer transition-all flex items-center justify-center ${
                  canvasScale < 1
                    ? 'text-emerald-700 bg-emerald-100 hover:bg-emerald-200'
                    : 'text-slate-500 hover:text-slate-900 hover:bg-slate-200/70'
                }`}
                title={canvasScale < 1 ? "Restablecer a escala normal (100%)" : "Expandir lienzo en vista panorámica completa"}
                aria-label={canvasScale < 1 ? "Restablecer lienzo" : "Vista panorámica"}
              >
                {canvasScale < 1 ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
              </button>
            </div>
            )}
          </div>
        </div>
        )}

        {/* MAIN VIEWPORT */}
        <main className={`flex-1 flex flex-col relative bg-white overflow-hidden h-full min-h-0 ${isEventExperience ? 'pb-0' : 'pb-[calc(3.75rem+env(safe-area-inset-bottom))] md:pb-0'}`}>
          <React.Suspense fallback={(
            <div className="flex-1 flex items-center justify-center bg-slate-50 text-slate-500 text-sm font-semibold">
              <Loader2 size={18} className="animate-spin mr-2 text-emerald-600" /> Cargando módulo…
            </div>
          )}>
          {mainView === 'events' ? (
            !canAccessEvents ? (
              <RestrictedAppView
                appKey="events"
                appName="Operación comercial"
                categoryName="Ventas & Clientes"
                icon={<CalendarDays size={28} />}
                userProfile={userProfile}
                features={[
                  "Gestión de conferencias, congresos y meetups en vivo",
                  "Venta de tickets, inscripciones y pasarelas de pago",
                  "Acreditación por QR, badges y control de acceso presencial",
                  "Comité académico, ponentes, agenda y moderación"
                ]}
              />
            ) : (
            <EventExperience
              state={eventOperations}
              onChange={setEventOperations}
              events={eventPortfolio.events}
              activeEventId={eventPortfolio.activeEventId}
              onSelectEvent={(eventId) => setEventPortfolio((current) => selectEventInPortfolio(current, eventId))}
              onCreateEvent={() => setEventPortfolio((current) => addEventToPortfolio(current))}
              onDeleteEvent={handleDeleteEvent}
              sponsors={sponsorsForRelations}
              userName={userProfile.name}
              initialArea={eventResumeArea}
              onPublishLanding={handlePublishLanding}
              onUnpublishLanding={handleUnpublishLanding}
              onPrepareContent={() => {
                const generatedItems = mapEventPlanToStudio(eventOperations);
                const generatedIds = new Set(generatedItems.map((item) => item.id));
                handleUpdateStudioItems([
                  ...studioItems.filter((item) => !generatedIds.has(item.id)),
                  ...generatedItems,
                ]);
                setAdvancedModuleView('studio');
                setMainView('studio');
              }}
              onNavigate={(view, returnArea) => {
                if (view === 'crm') setCrmReturnArea(returnArea ?? 'home');
                setAdvancedModuleView(view);
                setMainView(view);
                if (view === 'canvas') setActiveLayer('global');
              }}
            />
            )
          ) : mainView === 'crm' ? (
            // --- CRM OPPORTUNITIES PIPELINE SECTION ---
            advancedModuleView !== 'crm' && !crmReturnArea ? (
              <ModuleExperienceHome
                eyebrow="Seguimiento comercial"
                title="Personas y próximos pasos"
                description="Organiza conversaciones, propuestas y decisiones pendientes sin confundirlas con dinero ya cobrado."
                explanation="CRM significa seguimiento de relaciones y oportunidades. Los montos del tablero son estimaciones comerciales internas; tus ingresos reales se confirman en pagos y facturación."
                icon={Users}
                metrics={[
                  { label: 'En seguimiento', value: opportunities.filter((item) => item.status === 'open').length, helper: 'Conversaciones o propuestas que siguen abiertas.' },
                  { label: 'Con fecha prevista', value: opportunities.filter((item) => item.status === 'open' && item.expectedCloseDate).length, helper: 'Casos que ya tienen una próxima fecha definida.' },
                  { label: 'Responsables', value: teamMembers.length, helper: 'Personas del equipo que pueden hacerse cargo.' },
                ]}
                tasks={[
                  { title: 'Ver contactos', description: 'Encuentra a cada persona y su contexto.', icon: Users },
                  { title: 'Registrar seguimiento', description: 'Anota llamadas, mensajes y próximos pasos.', icon: MessageSquareHeart },
                  { title: 'Revisar resultados', description: 'Distingue oportunidades de ventas confirmadas.', icon: TrendingUp },
                ]}
                primaryActionLabel="Abrir seguimiento"
                onOpenOperations={() => setAdvancedModuleView('crm')}
              />
            ) : (
              <ModuleOperationsFrame
                backLabel={crmReturnArea === 'people' ? 'Volver a personas' : crmReturnArea ? 'Volver a Mi oferta' : 'Volver a seguimiento'}
                contextLabel="Vista completa"
                onBack={() => {
                  if (crmReturnArea) {
                    setEventResumeArea(crmReturnArea);
                    setMainView('events');
                  } else {
                    setAdvancedModuleView(null);
                  }
                }}
              >
                <CrmSection
                  opportunities={opportunities}
                  onAddOpportunity={handleAddOpportunity}
                  onUpdateOpportunity={handleUpdateOpportunity}
                  onDeleteOpportunity={handleDeleteOpportunity}
                  onImportOpportunities={handleImportOpportunities}
                  teamMembers={teamMembers}
                  nodes={nodes}
                />
              </ModuleOperationsFrame>
            )
          ) : mainView === 'studio' ? (
            // --- CONTENT PRODUCTION STUDIO SECTION ---
            !canAccessStudio ? (
              <RestrictedAppView
                appKey="studio"
                appName="Content Studio"
                categoryName="Creación & Copywriting"
                icon={<Clapperboard size={28} />}
                userProfile={userProfile}
                features={[
                  "Planificador editorial multiplataforma",
                  "Generador de guiones virales y estructuras de venta",
                  "Formatos listos para TikTok, Reels, YouTube y LinkedIn",
                  "Control de estados de producción y workflow de equipo"
                ]}
              />
            ) : advancedModuleView !== 'studio' ? (
              <ModuleExperienceHome
                eyebrow="Contenido"
                title="Contenido para publicar"
                description="Convierte ideas en piezas concretas para redes, email, anuncios o tu proceso comercial."
                explanation="Studio es tu mesa de trabajo interna: aquí preparas textos, guiones y publicaciones. Nada se publica solo y ninguna persona externa ve los borradores."
                icon={Clapperboard}
                metrics={[
                  { label: 'Ideas', value: studioItems.filter((item) => item.status === 'idea').length, helper: 'Temas que todavía no empezaste a producir.' },
                  { label: 'En preparación', value: studioItems.filter((item) => ['scripting', 'production', 'ready'].includes(item.status)).length, helper: 'Piezas que estás escribiendo, diseñando o revisando.' },
                  { label: 'Publicados', value: studioItems.filter((item) => item.status === 'published').length, helper: 'Contenido que ya salió por un canal.' },
                ]}
                tasks={[
                  { title: 'Guardar una idea', description: 'Anota el tema antes de desarrollarlo.', icon: Sparkles },
                  { title: 'Preparar una pieza', description: 'Escribe el contenido y define dónde publicarlo.', icon: Clapperboard },
                  { title: 'Ver lo publicado', description: 'Consulta fechas y alcance real registrado.', icon: CheckCircle2 },
                ]}
                primaryActionLabel="Abrir Studio"
                onOpenOperations={() => setAdvancedModuleView('studio')}
              />
            ) : (
              <ModuleOperationsFrame backLabel="Volver a Contenido" contextLabel="Vista completa" onBack={() => setAdvancedModuleView(null)}>
                <StudioSection
                  teamMembers={teamMembers}
                  stagesOrder={STAGES_ORDER}
                  activeStageFilter={activeLayer === 'global' ? 'all' : (activeLayer as FunnelStage)}
                  onSelectStageFilter={(st) => {
                    setActiveLayer(st === 'all' ? 'global' : st);
                  }}
                  items={studioItems}
                  onUpdateItems={handleUpdateStudioItems}
                  organizationId={userProfile.organizationId}
                />
              </ModuleOperationsFrame>
            )
          ) : mainView === 'digital_ai' ? (
            // --- DIGITAL AI ASSETS & APPS SECTION ---
            !canAccessDigital ? (
              <RestrictedAppView
                appKey="aiapps"
                appName="Digital AI Apps"
                categoryName="Inteligencia Artificial & Automatización"
                icon={<Cpu size={28} />}
                userProfile={userProfile}
                features={[
                  "Modelos de IA afinados para crecimiento y prospección",
                  "Playground de ejecución y testing en tiempo real",
                  "Historial unificado de ejecuciones y trazabilidad",
                  "Integración con webhooks y flujos del embudo"
                ]}
              />
            ) : advancedModuleView !== 'digital_ai' ? (
              <ModuleExperienceHome
                eyebrow="Asistentes digitales"
                title="IA para tareas repetitivas"
                description="Usa asistentes preparados para redactar, ordenar información o acelerar procesos específicos."
                explanation="Digital no es una red social ni una pantalla pública. Es el lugar donde configuras y pruebas ayudantes de IA antes de usarlos en tu trabajo."
                icon={Cpu}
                metrics={[
                  { label: 'Asistentes', value: digitalAiAssets.length, helper: 'Ayudantes disponibles para tareas concretas.' },
                  { label: 'Listos para usar', value: digitalAiAssets.filter((item) => item.status === 'deployed').length, helper: 'Configuraciones marcadas como activas.' },
                  { label: 'Pruebas guardadas', value: digitalAiExecutionHistory.length, helper: 'Resultados que puedes revisar y comparar.' },
                ]}
                tasks={[
                  { title: 'Elegir un asistente', description: 'Busca la tarea que quieres resolver.', icon: Cpu },
                  { title: 'Probar una instrucción', description: 'Comprueba el resultado antes de usarlo.', icon: Sparkles },
                  { title: 'Revisar el historial', description: 'Vuelve a respuestas y pruebas anteriores.', icon: CheckSquare },
                ]}
                primaryActionLabel="Ver asistentes"
                onOpenOperations={() => setAdvancedModuleView('digital_ai')}
              />
            ) : (
              <ModuleOperationsFrame backLabel="Volver a Asistentes" contextLabel="Vista completa" onBack={() => setAdvancedModuleView(null)}>
                <DigitalAiAppsSection
                  nodes={nodes}
                  teamMembers={teamMembers}
                  stagesOrder={STAGES_ORDER}
                  assets={digitalAiAssets}
                  onUpdateAssets={handleUpdateDigitalAiAssets}
                  executionHistory={digitalAiExecutionHistory}
                  onUpdateExecutionHistory={handleUpdateDigitalAiHistory}
                  organizationId={userProfile.organizationId}
                  onOpenCanvasWithNode={(nodeId) => {
                    setAdvancedModuleView('canvas');
                    setMainView('canvas');
                    const targetNode = nodes.find(n => n.id === nodeId);
                    if (targetNode) {
                      setActiveLayer(targetNode.stage);
                    }
                    setSelectedNodeIds([nodeId]);
                  }}
                />
              </ModuleOperationsFrame>
           )
         ) : mainView === 'sponsors' ? (
            // --- SPONSORS & PATROCINADORES PIPELINE SECTION ---
            !canAccessSponsors ? (
              <RestrictedAppView
                appKey="sponsors"
                appName="Patrocinadores & Sponsors"
                categoryName="Monetización & Alianzas"
                icon={<Flame size={28} />}
                userProfile={userProfile}
                features={[
                  "Gestión de marcas, tiers y paquetes comerciales",
                  "Inventario de activaciones y seguimiento de entregables",
                  "Pipeline de negociación y estados de cobranza",
                  "Reportes de retorno de inversión para sponsors"
                ]}
              />
            ) : advancedModuleView !== 'sponsors' ? (
              <ModuleExperienceHome
                eyebrow="Alianzas"
                title="Sponsors, aliados y difusión"
                description="Organiza quién puede aportar dinero, productos, espacios, contactos o visibilidad a tu proyecto."
                explanation="Este módulo no significa que debas gastar. También sirve para invitar referentes, pedir difusión, registrar apoyos gratuitos o intercambiar valor sin dinero."
                icon={Flame}
                metrics={[
                  { label: 'Marcas y aliados', value: sponsorOperations.brands.length, helper: 'Organizaciones o personas que podrías convocar.' },
                  { label: 'Conversaciones', value: sponsorOperations.opportunities.filter((item) => !['lost', 'closed_won'].includes(item.stage)).length, helper: 'Acuerdos que todavía se están evaluando.' },
                  { label: 'Activaciones', value: sponsorOperations.activations.filter((item) => ['approved', 'scheduled', 'live'].includes(item.status)).length, helper: 'Acciones aprobadas, programadas o en curso.' },
                ]}
                tasks={[
                  { title: 'Invitar a un aliado', description: 'Registra a quién quieres convocar y por qué.', icon: Users },
                  { title: 'Definir el intercambio', description: 'Aclara qué aporta cada parte, incluso si es gratuito.', icon: CheckSquare },
                  { title: 'Dar seguimiento', description: 'Guarda respuestas, compromisos y próximas fechas.', icon: MessageSquareHeart },
                ]}
                primaryActionLabel="Gestionar aliados"
                onOpenOperations={() => setAdvancedModuleView('sponsors')}
              />
            ) : (
              <ModuleOperationsFrame backLabel="Volver a Alianzas" contextLabel="Vista completa" onBack={() => setAdvancedModuleView(null)}>
                <SponsorsSection
                  state={sponsorOperations}
                  onChange={setSponsorOperations}
                  legacySponsors={sponsors}
                  teamMembers={teamMembers}
                  projectName={clientNames[activeClientIndex] || 'Proyecto actual'}
                  eventOperations={eventOperations}
                />
              </ModuleOperationsFrame>
            )
          ) : mainView === 'metrics' ? (
            // --- METRICS, ROI & STAGE CONTROL SECTION ---
            advancedModuleView !== 'metrics' ? (
              <ModuleExperienceHome
                eyebrow="Resultados"
                title="Qué está funcionando"
                description="Mira pocas cifras importantes y entiende si conviene invertir, ajustar o sostener lo que ya haces."
                explanation="Las métricas separan datos reales de proyecciones. Una proyección es una simulación para decidir; no significa que ese dinero ya se haya cobrado."
                icon={BarChart3}
                metrics={[
                  { label: 'Inversión cargada', value: `${eventOperations.event.defaultCurrency} ${new Intl.NumberFormat('es-AR').format(parameters.adSpend)}`, helper: 'Presupuesto indicado para atraer personas.' },
                  { label: 'Facturación proyectada', value: `${eventOperations.event.defaultCurrency} ${new Intl.NumberFormat('es-AR').format(metrics.totalRevenue)}`, helper: 'Estimación basada en tus tasas y precios actuales.' },
                  { label: 'Retorno estimado', value: parameters.adSpend > 0 ? `${(metrics.totalRevenue / parameters.adSpend).toFixed(1)}x` : '—', helper: 'Cuánto podría volver por cada unidad invertida.' },
                ]}
                tasks={[
                  { title: 'Entender una cifra', description: 'Consulta qué significa y de dónde sale.', icon: HelpCircle },
                  { title: 'Cambiar un supuesto', description: 'Prueba inversión, precio o conversión.', icon: Sliders },
                  { title: 'Comparar escenarios', description: 'Distingue una proyección de un dato real.', icon: TrendingUp },
                ]}
                primaryActionLabel="Ver análisis completo"
                onOpenOperations={() => setAdvancedModuleView('metrics')}
              />
            ) : (
              <ModuleOperationsFrame backLabel="Volver a Resultados" contextLabel="Vista completa" onBack={() => setAdvancedModuleView(null)}>
                <MetricsSection
                  parameters={parameters}
                  onParamChange={handleParamChange}
                  nodes={nodes}
                  teamMembers={teamMembers}
                  sponsors={sponsorsForRelations}
                  opportunities={opportunities}
                  clientName={clientNames[activeClientIndex] || 'Estrategia de Embudo'}
                  activeStageFilter={activeLayer}
                  onSelectStageFilter={(st) => {
                    setActiveLayer(st);
                  }}
                  onBackToCanvas={(targetStage) => {
                    setAdvancedModuleView('canvas');
                    setMainView('canvas');
                    if (targetStage && targetStage !== 'global') {
                      setActiveLayer(targetStage);
                    }
                  }}
                  stagesOrder={STAGES_ORDER}
                />
              </ModuleOperationsFrame>
            )
          ) : mainView === 'canvas' ? (
            <SimplePlanSection
              nodes={nodes}
              links={links}
              onAddTool={() => setAddToolModalStage('all')}
              onEditTool={(node) => setConfigureNodeModal(node)}
              onDeleteTool={deleteNode}
           />
          ) : null}
         </React.Suspense>
        </main>
      </div>

      {/* FLOATING CANVA-STYLE ALIGNMENT & JUSTIFY TOOLBAR WHEN MULTIPLE NODES ARE SELECTED */}
      {selectedNodeIds.length > 1 && (
        <div className="fixed bottom-16 md:bottom-8 left-1/2 -translate-x-1/2 z-50 bg-slate-900/95 backdrop-blur-md text-white border border-slate-700/80 px-4 py-2 rounded-2xl shadow-2xl flex items-center gap-2 max-w-[95vw] overflow-x-auto animate-slideUp">
          <div className="flex items-center gap-1.5 pr-2 border-r border-slate-700 shrink-0">
            <span className="w-5 h-5 rounded-full bg-emerald-500 text-slate-950 text-[10px] font-black flex items-center justify-center shadow-xs">
              {selectedNodeIds.length}
            </span>
            <span className="text-xs font-extrabold text-slate-200 hidden sm:inline">
              Seleccionados
            </span>
          </div>

          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={handleCopy}
              className="bg-slate-800 hover:bg-slate-700 text-slate-100 px-2.5 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer border border-slate-700 hover:border-amber-500 shadow-xs"
              title="Copiar elementos seleccionados (Ctrl + C)"
            >
              <Copy size={14} className="text-amber-400" />
              <span>Copiar</span>
            </button>

            <button
              onClick={handlePaste}
              disabled={!clipboardRef.current || clipboardRef.current.nodes.length === 0}
              className={`px-2.5 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 border shadow-xs ${
                clipboardRef.current && clipboardRef.current.nodes.length > 0
                  ? 'bg-slate-800 hover:bg-slate-700 text-slate-100 border-slate-700 hover:border-amber-500 cursor-pointer'
                  : 'bg-slate-800/50 text-slate-500 border-slate-800 cursor-not-allowed opacity-60'
              }`}
              title="Pegar elementos copiados (Ctrl + V)"
            >
              <ClipboardPaste size={14} className={clipboardRef.current && clipboardRef.current.nodes.length > 0 ? "text-amber-400" : "text-slate-500"} />
              <span>Pegar</span>
            </button>

            <button
              onClick={deleteSelectedNodes}
              className="bg-rose-600 hover:bg-rose-700 text-white px-2.5 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer shadow-xs border border-rose-500 active:scale-95"
              title="Eliminar herramientas seleccionadas (Tecla Supr / Backspace)"
            >
              <Trash2 size={14} />
              <span>Eliminar</span>
            </button>

            <button
              onClick={distributeSelectedNodesHorizontal}
              className="bg-slate-800 hover:bg-emerald-600 text-slate-100 hover:text-slate-950 px-2.5 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer border border-slate-700 hover:border-emerald-500 shadow-xs"
              title="Iguala el espacio lateral entre las burbujas seleccionadas"
            >
              <MoveHorizontal size={14} className="text-emerald-400" />
              <span>Espaciar Lateral</span>
            </button>

            <button
              onClick={distributeSelectedNodesVertical}
              className="bg-slate-800 hover:bg-emerald-600 text-slate-100 hover:text-slate-950 px-2.5 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer border border-slate-700 hover:border-emerald-500 shadow-xs"
              title="Iguala el espacio vertical entre las burbujas seleccionadas"
            >
              <MoveVertical size={14} className="text-emerald-400" />
              <span>Espaciar Vertical</span>
            </button>

            <button
              onClick={alignSelectedNodesVerticalCenter}
              className="bg-slate-800 hover:bg-blue-600 text-slate-100 hover:text-white px-2.5 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer border border-slate-700 hover:border-blue-500 shadow-xs"
              title="Alinea todas las burbujas en la misma altura (centro horizontal)"
            >
              <AlignJustify size={14} className="text-blue-400 rotate-90" />
              <span className="hidden md:inline">Alinear Horizontal</span>
            </button>

            <button
              onClick={alignSelectedNodesHorizontalCenter}
              className="bg-slate-800 hover:bg-blue-600 text-slate-100 hover:text-white px-2.5 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer border border-slate-700 hover:border-blue-500 shadow-xs"
              title="Alinea todas las burbujas en la misma columna (centro vertical)"
            >
              <AlignJustify size={14} className="text-blue-400" />
              <span className="hidden md:inline">Alinear Vertical</span>
            </button>

            <button
              onClick={chainConnectSelectedNodes}
              className="bg-emerald-600 hover:bg-emerald-500 text-slate-950 px-2.5 py-1.5 rounded-xl text-xs font-black transition-all flex items-center gap-1.5 cursor-pointer shadow-xs"
              title="Conectar en cadena en el orden seleccionado"
            >
              <LinkIcon size={14} />
              <span className="hidden sm:inline">Enlazar</span>
            </button>

            <select
              onChange={(e) => {
                if (e.target.value) {
                  connectSelectedToTarget(e.target.value);
                  e.target.value = '';
                }
              }}
              defaultValue=""
              className="bg-emerald-600 hover:bg-emerald-500 text-slate-950 font-black px-2.5 py-1.5 rounded-xl text-xs transition-all cursor-pointer shadow-xs border-none outline-none"
              title="Conectar todos los seleccionados a un módulo destino"
            >
              <option value="" disabled className="bg-slate-900 text-white font-normal">Conectar Todos a...</option>
              {nodes.map(n => (
                <option key={n.id} value={n.id} disabled={selectedNodeIds.includes(n.id)} className="bg-slate-900 text-white font-normal">
                  {n.name} ({n.stage})
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={() => setSelectedNodeIds([])}
            className="p-1.5 hover:bg-slate-800 text-slate-400 hover:text-slate-200 rounded-xl transition-colors cursor-pointer ml-1"
            title="Desmarcar Selección"
          >
            <X size={15} />
          </button>
        </div>
      )}

      <React.Suspense fallback={(
        <div className="fixed inset-0 z-[60] bg-slate-950/35 backdrop-blur-sm flex items-center justify-center">
          <div className="bg-white px-4 py-3 rounded-xl shadow-xl text-xs font-bold text-slate-700 flex items-center gap-2">
            <Loader2 size={16} className="animate-spin text-emerald-600" /> Cargando…
          </div>
        </div>
      )}>
      {/* --- UNIVERSAL TOOL CATALOG & CUSTOM TOOL CREATOR --- */}
      {addToolModalStage && (
      <ToolCatalogModal
        isOpen={!!addToolModalStage}
        initialDestinationStage={addToolModalStage !== 'all'
          ? addToolModalStage
          : (activeLayer !== 'global' ? activeLayer : 'personal')}
        stages={STAGES_ORDER.map(({ stage, label, color }) => ({ stage, label, color }))}
        builtInTemplates={AVAILABLE_APP_TEMPLATES}
        customTemplates={customToolTemplates}
        onClose={() => setAddToolModalStage(null)}
        onAddTool={handleAddToolProxy}
        onCreateTool={handleCreateCustomTool}
        onDeleteCustomTool={handleDeleteCustomTool}
      />
      )}
      {/* --- LOGIN / AUTHENTICATION MODAL --- */}
      {showLoginModal && (
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        accessDeniedReason={accessDeniedReason}
      />
      )}

      {/* --- GOOGLE SHEETS DB MODAL --- */}
      {showGoogleSheetsModal && (
      <GoogleSheetsModal
        isOpen={showGoogleSheetsModal}
        onClose={() => setShowGoogleSheetsModal(false)}
        nodes={nodes}
        links={links}
        parameters={parameters}
        clientName={activeClientName}
      />
      )}

      {/* --- FIREBASE FIRESTORE CLOUD SYNC MODAL --- */}
      {showFirebaseModal && (
      <FirebaseSyncModal
        isOpen={showFirebaseModal}
        onClose={() => setShowFirebaseModal(false)}
        userProfile={userProfile}
        currentFunnelData={{
          clientName: activeClientName,
          clientDesc: activeClientDesc,
          nodes,
          links,
          parameters,
          customToolTemplates
        }}
        onApplyLoadedFunnel={(loaded) => {
          pushSnapshot();
          setNodes(loaded.nodes);
          setLinks(loaded.links);
          setParameters(loaded.parameters);
          if (Array.isArray(loaded.customToolTemplates)) {
            setCustomToolTemplates(loaded.customToolTemplates);
          }
          saveClientState(
            loaded.nodes,
            loaded.links,
            loaded.parameters,
            activeClientIndex,
            activeLayer,
            undefined,
            loaded.customToolTemplates || customToolTemplates,
          );
        }}
      />
      )}

      {/* --- TEAM MEMBERS MANAGEMENT MODAL --- */}
      {showTeamModal && (
      <TeamMembersModal
        isOpen={showTeamModal}
        onClose={() => setShowTeamModal(false)}
        teamMembers={teamMembers}
        onUpdateTeamMembers={setTeamMembers}
        nodes={nodes}
      />
      )}

      {/* --- CONTEXT-AWARE TOOL MODULE DEVELOPMENT MODAL (+1% KPI) --- */}
      {configureNodeModal && (
      <ToolModuleModal
        isOpen={!!configureNodeModal}
        onClose={() => setConfigureNodeModal(null)}
        node={configureNodeModal}
        teamMembers={teamMembers}
        onUpdateNode={handleUpdateNodeFromModal}
      />
      )}

      {/* --- USER PROFILE MANAGEMENT MODAL --- */}
      {showUserProfileModal && (
      <UserProfileModal
        isOpen={showUserProfileModal}
        onClose={() => setShowUserProfileModal(false)}
        userProfile={userProfile}
        onUpdateProfile={(updated) => {
          setUserProfile(updated);
          localStorage.setItem('grosym_user_auth', JSON.stringify(updated));
        }}
        onLogout={handleLogout}
      />
      )}

      {/* --- ONBOARDING WIZARD & MVE PRE-EMBUDO GENERATOR --- */}
      {showOnboardingWizard && (
      <OnboardingWizard
        isOpen={showOnboardingWizard}
        onClose={() => setShowOnboardingWizard(false)}
        onComplete={handleOnboardingComplete}
        initialProfile={userProfile}
        onLogout={handleLogout}
      />
      )}

      {/* --- INTERACTIVE GUIDED PRODUCT TOUR TOOLTIPS --- */}
      {showProductTour && (
      <ProductTourTooltips
        isOpen={showProductTour}
        onClose={() => setShowProductTour(false)}
        onOpenTeamModal={() => setShowTeamModal(true)}
        onOpenSheetsModal={() => setShowGoogleSheetsModal(true)}
      />
      )}

      {/* --- EDIT CLIENT NAME MODAL --- */}
      {editingClientIndex !== null && (
        <div className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-xs flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full border border-slate-200 shadow-2xl relative">
            <button
              onClick={() => setEditingClientIndex(null)}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 p-1 rounded-lg cursor-pointer"
            >
              <X size={16} />
            </button>

            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-emerald-100 text-emerald-800 flex items-center justify-center font-bold">
                <Pencil size={16} />
              </div>
              <div>
                <h3 className="text-sm font-black text-slate-900">Editar Nombre de Cliente</h3>
                <p className="text-[10px] text-slate-500 font-medium">Personaliza el nombre de esta estrategia</p>
              </div>
            </div>

            <input
              type="text"
              value={tempClientName}
              onChange={(e) => setTempClientName(e.target.value)}
              placeholder="Nombre del Cliente..."
              className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 mb-4"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveClientName();
                if (e.key === 'Escape') setEditingClientIndex(null);
              }}
            />

            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => setEditingClientIndex(null)}
                className="px-3 py-1.5 rounded-xl border border-slate-200 text-xs font-bold text-slate-600 hover:bg-slate-100 transition-all cursor-pointer"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveClientName}
                className="px-4 py-1.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black text-xs shadow-md transition-all cursor-pointer"
              >
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- CONFIRM DELETE PROJECT MODAL --- */}
      {projectToDeleteIndex !== null && (
        <div className="fixed inset-0 z-50 bg-slate-950/70 backdrop-blur-xs flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-white rounded-3xl p-6 max-w-sm w-full border border-slate-200 shadow-2xl relative text-center">
            <button
              onClick={() => setProjectToDeleteIndex(null)}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 p-1.5 rounded-lg cursor-pointer"
            >
              <X size={16} />
            </button>

            <div className="w-12 h-12 rounded-2xl bg-rose-100 text-rose-600 flex items-center justify-center mx-auto mb-3 shadow-xs">
              <Trash2 size={24} />
            </div>

            <h3 className="text-base font-black text-slate-900 mb-1">
              ¿Eliminar este proyecto?
            </h3>

            <p className="text-xs text-slate-500 font-medium mb-3">
              Estás a punto de eliminar el proyecto <strong className="text-slate-900">"{clientNames[projectToDeleteIndex] || 'Cliente'}"</strong> y su configuración asociada.
            </p>

            <div className="bg-rose-50 border border-rose-100 rounded-xl p-2.5 mb-5 text-[11px] text-rose-700 font-medium text-left">
              ⚠️ Esta acción no se puede deshacer. Por seguridad te pedimos confirmar.
            </div>

            <div className="flex items-center justify-center gap-2.5">
              <button
                onClick={() => setProjectToDeleteIndex(null)}
                className="flex-1 py-2 rounded-xl border border-slate-200 text-xs font-extrabold text-slate-700 hover:bg-slate-100 transition-all cursor-pointer"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleDeleteProject(projectToDeleteIndex)}
                className="flex-1 py-2 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-black text-xs shadow-md transition-all cursor-pointer flex items-center justify-center gap-1"
              >
                <Trash2 size={13} />
                <span>Sí, eliminar</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- FUNNEL AUTOMATION MODAL (CONTENT, ADS, REPORTS) --- */}
      {automationModalType && (
        <FunnelAutomationModal
          isOpen={true}
          onClose={() => setAutomationModalType(null)}
          automationType={automationModalType}
          activeStage={automationModalStage}
          clientName={clientNames[activeClientIndex] || 'Cliente'}
          parameters={parameters}
          nodes={nodes}
          onAddResult={handleAddGeneratedResult}
        />
      )}

      {activationPickerStage && (
        <div className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setActivationPickerStage(null)}>
          <div className="w-full max-w-lg bg-white rounded-2xl border border-slate-200 shadow-2xl overflow-hidden" onClick={(event) => event.stopPropagation()}>
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h3 className="font-black text-slate-900">Activaciones de la etapa</h3>
                <p className="text-xs text-slate-500">Selecciona la automatización que quieres revisar.</p>
              </div>
              <button type="button" onClick={() => setActivationPickerStage(null)} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500">
                <X size={16} />
              </button>
            </div>
            <div className="p-4 space-y-2 max-h-[60vh] overflow-y-auto">
              {activations.filter(item => item.stage === activationPickerStage).map(item => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => {
                    setActivationPickerStage(null);
                    handleOpenEditActivation(item);
                  }}
                  className="w-full text-left p-3 rounded-xl border border-slate-200 hover:border-amber-400 hover:bg-amber-50 transition-colors"
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-bold text-slate-900">{item.name}</span>
                    <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${item.active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}`}>
                      {item.active ? 'Activa' : 'Pausada'}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">{item.channel} · {item.executionMode} · {item.action}</p>
                </button>
              ))}
            </div>
            <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 flex justify-end">
              <button
                type="button"
                onClick={() => {
                  const stage = activationPickerStage;
                  const stageIndex = STAGES_ORDER.findIndex(item => item.stage === stage);
                  setActivationPickerStage(null);
                  handleOpenAddActivation(stage, STAGES_ORDER[stageIndex + 1]?.stage);
                }}
                className="px-3 py-2 rounded-xl bg-amber-400 hover:bg-amber-300 text-slate-950 text-xs font-black flex items-center gap-1.5"
              >
                <Plus size={14} /> Nueva activación
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- STAGE ACTIVATION MODAL (CUANDO X -> HACEMOS Y) --- */}
      {activationModalOpen && (
        <StageActivationModal
          isOpen={activationModalOpen}
          onClose={() => setActivationModalOpen(false)}
          stage={activationModalStage}
          targetStage={activationModalTargetStage}
          stageName={STAGES_ORDER.find(s => s.stage === activationModalStage)?.label || activationModalStage}
          stageColor={STAGES_ORDER.find(s => s.stage === activationModalStage)?.color || '#f59e0b'}
          existingActivation={editingActivation}
          onSaveActivation={handleSaveActivation}
          onDeleteActivation={handleDeleteActivation}
        />
      )}
      </React.Suspense>

      {/* --- MOBILE FLOATING BOTTOM NAVIGATION BAR --- */}
      <nav className={`fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-xl border-t border-slate-200 px-2 pt-1.5 pb-[max(0.375rem,env(safe-area-inset-bottom))] items-center justify-around z-40 md:hidden shadow-[0_-8px_24px_rgba(15,23,42,0.08)] ${isMobileUiHidden || isEventExperience ? 'hidden' : 'flex'}`} aria-label="Navegación principal móvil">
        <button
          onClick={() => {
            setAdvancedModuleView(null);
            setMainView('events');
          }}
          className={`min-w-14 min-h-12 rounded-xl flex flex-col items-center justify-center gap-0.5 text-[9px] font-bold cursor-pointer transition-all active:scale-95 ${mainView === 'events' ? 'text-slate-950 bg-slate-100 font-black' : 'text-slate-500'}`}
          aria-label="Abrir inicio comercial"
        >
          <CalendarDays size={18} />
          <span>Inicio</span>
        </button>

        <button
          onClick={() => {
            setAdvancedModuleView(null);
            setMainView('canvas');
            setActiveLayer('global');
          }}
          className={`min-w-16 min-h-12 rounded-xl flex flex-col items-center justify-center gap-0.5 text-[10px] font-bold cursor-pointer transition-all active:scale-95 ${mainView === 'canvas' ? 'text-slate-950 bg-slate-100 font-black' : 'text-slate-500'}`}
          aria-label="Abrir lienzo"
        >
          <Layers size={18} />
          <span>Lienzo</span>
        </button>

        <button
          onClick={() => {
            setAdvancedModuleView(null);
            setMainView('metrics');
          }}
          className={`min-w-16 min-h-12 rounded-xl flex flex-col items-center justify-center gap-0.5 text-[10px] font-bold cursor-pointer transition-all active:scale-95 ${mainView === 'metrics' ? 'text-slate-950 bg-slate-100 font-black' : 'text-slate-500'}`}
          aria-label="Abrir métricas"
        >
          <Sliders size={18} />
          <span>Métricas</span>
        </button>

        <button
          onClick={() => {
            setAdvancedModuleView(null);
            setCrmReturnArea(null);
            setMainView('crm');
          }}
          className={`min-w-16 min-h-12 rounded-xl flex flex-col items-center justify-center gap-0.5 text-[10px] font-bold cursor-pointer transition-all active:scale-95 ${mainView === 'crm' ? 'text-slate-950 bg-slate-100 font-black' : 'text-slate-500'}`}
          aria-label="Abrir CRM"
        >
          <div className={`w-7 h-7 rounded-xl flex items-center justify-center shadow-xs transition-colors ${mainView === 'crm' ? 'bg-slate-950 text-emerald-400' : 'bg-slate-100 text-slate-700'}`}>
            <DollarSign size={16} className="stroke-[2.5]" />
          </div>
          <span>CRM</span>
        </button>

      </nav>

    </div>
  );
}
